import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Eye,
  FileSpreadsheet,
  MessageCircle,
  MousePointerClick,
  RefreshCcw,
  Search,
  Send,
  Users,
  Wand2,
  X,
  ChevronDown,
  Calendar,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  LabelList,
} from "recharts";
import { Card } from "../../components/ui/card";
import { roundCheckboxClass, roundCheckboxStyle } from "../../components/ui/roundCheckboxStyles";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import WhatsAppTemplatePreview from "../TemplateBuilder/components/WhatsAppTemplatePreview";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import axiosClient from "../../api/axiosClient";
import {
  getApprovedCampaignTemplateByName,
  listAllApprovedCampaignTemplates,
  syncApprovedCampaignTemplates,
} from "../../api/campaignTemplates";
import WorkspaceTitleHeader from "../../components/common/WorkspaceTitleHeader";
import StandaloneMediaUploader from "../Campaigns/components/StandaloneMediaUploader";
import {
  getCampaignBucketContacts,
  getCampaignReportSummary,
  previewCampaignRetargetAudience,
} from "../../api/campaignReportApi";
import { getCustomFieldDefinitions } from "../CustomFields/customFieldsApi";
import {
  extractCampaignAudienceTotalCount,
  getCampaignOutcomeCounts,
} from "./campaignOutcomeResponseUtils";

const DEFAULT_WINDOW_DAYS = 90;
const AUDIENCE_PAGE_SIZE = 50;
const buttonTypographyClass =
  "[&_button]:font-['Inter',ui-sans-serif,system-ui,sans-serif] [&_button]:text-sm [&_button]:font-normal";
const reportButtonClass =
  "inline-flex h-9 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-slate-200/70 disabled:cursor-not-allowed disabled:opacity-60";

function cx(...xs) {
  return xs.filter(Boolean).join(" ");
}

function parseWindowDaysParam(raw) {
  const normalized = String(raw ?? "").trim().toLowerCase();
  if (!normalized) return DEFAULT_WINDOW_DAYS;
  if (normalized === "all") return null;

  const parsed = Number.parseInt(normalized, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_WINDOW_DAYS;
}

const STAT_TABS = [
  "recipients",
  "sent",
  "delivered",
  "read",
  "clicked",
  "button_clicked",
  "replied",
  "failed",
];

const ALL_RECIPIENTS_SEGMENT = "ALL_RECIPIENTS";

const AUDIENCE_SEGMENTS = [
  { key: ALL_RECIPIENTS_SEGMENT, label: "All recipients" },
  { key: "DELIVERED_NOT_READ", label: "Delivered not read" },
  { key: "URL_CLICKED", label: "URL Clicked" },
  { key: "BUTTON_CLICKED", label: "Button Clicked" },
  { key: "READ_NOT_REPLIED", label: "Read not replied" },
  { key: "FAILED", label: "Failed" },
  { key: "REPLIED", label: "Replied" },
];

const RETARGET_SEGMENT_OPTIONS = [
  { key: "DELIVERED_NOT_READ", label: "Delivered not read" },
  { key: "URL_CLICKED", label: "URL Clicked" },
  { key: "BUTTON_CLICKED", label: "Button Clicked" },
  { key: "READ_NOT_REPLIED", label: "Read not replied" },
  { key: "FAILED", label: "Failed" },
  { key: "REPLIED", label: "Replied" },
];

const SEGMENT_FROM_TAB = {
  recipients: ALL_RECIPIENTS_SEGMENT,
  sent: ALL_RECIPIENTS_SEGMENT,
  delivered: "DELIVERED_NOT_READ",
  read: "READ_NOT_REPLIED",
  clicked: "URL_CLICKED",
  button_clicked: "BUTTON_CLICKED",
  failed: "FAILED",
  replied: "REPLIED",
};

const TAB_FROM_SEGMENT = {
  [ALL_RECIPIENTS_SEGMENT]: "recipients",
  DELIVERED_NOT_READ: "delivered",
  CLICKED: "clicked",
  URL_CLICKED: "clicked",
  BUTTON_CLICKED: "button_clicked",
  READ_NOT_REPLIED: "read",
  FAILED: "failed",
  REPLIED: "replied",
};

// Header kind helpers
const HK = Object.freeze({
  None: "none",
  Text: "text",
  Image: "image",
  Video: "video",
  Document: "document",
});
const HEADER_MEDIA_SOURCE = Object.freeze({
  Url: "url",
  Upload: "upload",
});
const isMediaHeader = (hk) =>
  hk === HK.Image || hk === HK.Video || hk === HK.Document;
const mediaLabel = (hk) =>
  hk === HK.Image
    ? "Image"
    : hk === HK.Video
      ? "Video"
      : "Document";

const normalizeHeaderKind = (t) => {
  const raw = (t.headerKind || t.HeaderKind || "").toString().toLowerCase();
  if (
    raw === HK.Image ||
    raw === HK.Video ||
    raw === HK.Document ||
    raw === HK.Text ||
    raw === HK.None
  ) {
    return raw;
  }
  return t.hasImageHeader || t.HasImageHeader ? HK.Image : HK.None;
};

function normalizeButtonsForTemplatePreview(buttons, buttonParams = []) {
  const list = Array.isArray(buttons) ? buttons : [];
  return list
    .map((button, idx) => {
      const typeRaw = String(
        button?.type ||
          button?.buttonType ||
          button?.ButtonType ||
          button?.subType ||
          button?.SubType ||
          ""
      ).toUpperCase();
      const text = String(
        button?.text || button?.buttonText || button?.ButtonText || "Button"
      );
      const staticValue = String(
        button?.value ||
          button?.ParameterValue ||
          button?.parameterValue ||
          button?.targetUrl ||
          ""
      );
      const value =
        buttonParams[idx] != null && String(buttonParams[idx]).trim()
          ? String(buttonParams[idx]).trim()
          : staticValue;

      if (typeRaw.includes("URL")) return { type: "URL", text, url: value };
      if (typeRaw.includes("PHONE"))
        return { type: "PHONE_NUMBER", text, phone_number: value };
      if (typeRaw.includes("QUICK")) return { type: "QUICK_REPLY", text };
      return { type: typeRaw || "QUICK_REPLY", text };
    })
    .slice(0, 3);
}

const normalizeMetaProvider = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  return normalized === "META" || normalized === "METACLOUD" || normalized === "META_CLOUD"
    ? "META_CLOUD"
    : "";
};

const RETARGETABLE_SEGMENTS = new Set(
  RETARGET_SEGMENT_OPTIONS.map((s) => s.key)
);

const ATTRIBUTE_OPERATORS_BY_TYPE = Object.freeze({
  Text: ["equals", "contains", "is_empty", "is_not_empty"],
  Number: ["equals", "is_empty", "is_not_empty"],
  Date: ["equals", "is_empty", "is_not_empty"],
  Boolean: ["equals", "is_empty", "is_not_empty"],
  SingleSelect: ["equals", "is_empty", "is_not_empty"],
  MultiSelect: ["equals", "is_empty", "is_not_empty"],
});

const ATTRIBUTE_OPERATOR_LABELS = Object.freeze({
  equals: "Equals",
  contains: "Contains",
  is_empty: "Is empty",
  is_not_empty: "Is not empty",
});

function createAttributeCondition() {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    fieldId: "",
    operator: "equals",
    value: "",
  };
}

function segmentLabel(key) {
  return AUDIENCE_SEGMENTS.find((s) => s.key === key)?.label || key;
}

function getAudienceStatusTone(status) {
  const normalized = String(status || "").trim().toUpperCase();
  if (!normalized) {
    return "text-slate-600";
  }
  if (
    normalized.includes("FAILED") ||
    normalized.includes("ERROR") ||
    normalized === "FAIL"
  ) {
    return "text-red-700";
  }
  if (normalized.includes("REPLIED") || normalized.includes("REPLY")) {
    return "text-pink-700";
  }
  if (normalized.includes("CLICK")) {
    return "text-amber-700";
  }
  if (normalized.includes("READ")) {
    return "text-blue-700";
  }
  if (normalized.includes("DELIVERED")) {
    return "text-emerald-700";
  }
  if (normalized.includes("SENT")) {
    return "text-teal-700";
  }
  return "text-slate-700";
}

function getRetargetEligibility(status) {
  const raw = String(status ?? "").trim();
  const s = raw.toUpperCase();
  if (!s) {
    return {
      ok: false,
      reason: "Campaign status is unknown, so retargeting is disabled.",
    };
  }

  const ok =
    s === "SENT" ||
    s === "COMPLETED" ||
    s === "FINISHED" ||
    s === "DONE" ||
    s.includes("SENT") ||
    s.includes("COMPLETE");

  if (ok) return { ok: true, reason: "" };
  return {
    ok: false,
    reason: `Retargeting is available only after the campaign is sent. Current status: ${raw}.`,
  };
}

function safeNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
}

function tryParseDate(x) {
  if (!x) return null;
  const d = new Date(x);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(x) {
  const d = tryParseDate(x);
  return d ? d.toLocaleString() : "-";
}

function formatDurationMs(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return "-";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 1) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return `${hours}h ${remMin}m`;
}

function safeJsonParse(x) {
  if (typeof x !== "string") return null;
  const t = x.trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

function extractTemplateBodyText(t) {
  if (!t) return "";

  const directCandidates = [
    t.body,
    t.bodyText,
    t.text,
    t.message,
    t.content,
    t.templateText,
    t.TemplateText,
  ];
  for (const c of directCandidates) {
    if (typeof c === "string" && c.trim()) return c;
  }

  const componentsRaw =
    t.components ?? t.Components ?? t.componentsJson ?? t.ComponentsJson ?? null;

  const components =
    Array.isArray(componentsRaw) ? componentsRaw : safeJsonParse(componentsRaw);

  if (Array.isArray(components)) {
    const body =
      components.find((c) => String(c?.type || c?.Type || "").toUpperCase() === "BODY") ??
      components.find((c) => typeof (c?.text ?? c?.Text) === "string") ??
      null;
    const text = body?.text ?? body?.Text;
    if (typeof text === "string" && text.trim()) return text;
  }

  return "";
}

function extractPlaceholdersFromText(text) {
  const out = new Set();
  const s = String(text ?? "");
  const re = /\{\{\s*(\d+)\s*\}\}/g;
  let m;
  while ((m = re.exec(s))) {
    const n = Number(m[1]);
    if (Number.isFinite(n) && n > 0) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

function getContactNameValue(c) {
  return (
    c?.name ||
    c?.contactName ||
    c?.contact?.name ||
    c?.fullName ||
    c?.displayName ||
    ""
  );
}

function getContactPhoneValue(c) {
  return (
    c?.phone ||
    c?.contactPhone ||
    c?.recipientNumber ||
    c?.to ||
    c?.recipient ||
    ""
  );
}

function pickProviderFromCampaignMeta(meta) {
  const nested =
    meta?.sender ||
    meta?.Sender ||
    meta?.whatsAppSender ||
    meta?.WhatsAppSender ||
    meta?.phoneNumber ||
    meta?.PhoneNumber ||
    null;

  const raw =
    meta?.provider ??
    meta?.Provider ??
    meta?.senderProvider ??
    meta?.SenderProvider ??
    meta?.whatsAppProvider ??
    meta?.WhatsAppProvider ??
    meta?.messagingProvider ??
    meta?.MessagingProvider ??
    nested?.provider ??
    nested?.Provider ??
    "";

  return normalizeMetaProvider(raw);
}

function pickPhoneNumberIdFromCampaignMeta(meta) {
  const nested =
    meta?.sender ||
    meta?.Sender ||
    meta?.whatsAppSender ||
    meta?.WhatsAppSender ||
    meta?.phoneNumber ||
    meta?.PhoneNumber ||
    null;

  const raw =
    meta?.phoneNumberId ??
    meta?.PhoneNumberId ??
    meta?.phoneNumberID ??
    meta?.PhoneNumberID ??
    meta?.senderPhoneNumberId ??
    meta?.SenderPhoneNumberId ??
    meta?.whatsAppPhoneNumberId ??
    meta?.WhatsAppPhoneNumberId ??
    meta?.wabaPhoneNumberId ??
    meta?.WabaPhoneNumberId ??
    nested?.phoneNumberId ??
    nested?.PhoneNumberId ??
    nested?.id ??
    nested?.Id ??
    "";

  return String(raw || "").trim();
}

function getCustomFieldValue(c, key) {
  const obj =
    c?.customFields ||
    c?.CustomFields ||
    c?.fields ||
    c?.Fields ||
    c?.meta?.customFields ||
    null;
  if (!obj || typeof obj !== "object") return "";
  return obj?.[key] ?? obj?.[String(key)] ?? "";
}

function RetargetModal({
  isOpen,
  onClose,
  campaignId,
  campaignStatus,
  campaignMeta,
  retargetAudience,
}) {
  const navigate = useNavigate();
  const { businessId } = useAuth();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showTemplateSearch, setShowTemplateSearch] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [templateQuery, setTemplateQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [templateDetails, setTemplateDetails] = useState(null);
  const [templateDetailsLoading, setTemplateDetailsLoading] = useState(false);
  const [templateDetailsError, setTemplateDetailsError] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [headerMediaUrl, setHeaderMediaUrl] = useState("");
  const [headerMediaSource, setHeaderMediaSource] = useState(null);
  const [headerMediaPreviewUrl, setHeaderMediaPreviewUrl] = useState("");
  const [buttonParams, setButtonParams] = useState([]);
  const [rawButtonParams, setRawButtonParams] = useState([]);
  const [headerKind, setHeaderKind] = useState(HK.None);
  const [requiresHeaderMediaUrl, setRequiresHeaderMediaUrl] = useState(false);
  const [attributeDefinitions, setAttributeDefinitions] = useState([]);

  const didRetargetNavigateRef = useRef(false);
  const headerMediaPreviewUrlRef = useRef("");

  const [variableConfigs, setVariableConfigs] = useState({});

  const [senders, setSenders] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [selectedSenderId, setSelectedSenderId] = useState("");

  // New scheduling states
  const [scheduleMode, setScheduleMode] = useState("now"); // "now" or "later"
  const [scheduledAt, setScheduledAt] = useState("");
  const [isTemplatePreviewOpen, setIsTemplatePreviewOpen] = useState(false);

  const replaceHeaderMediaPreview = (previewUrl) => {
    const previous = headerMediaPreviewUrlRef.current;
    if (previous?.startsWith("blob:") && previous !== previewUrl) {
      URL.revokeObjectURL(previous);
    }
    headerMediaPreviewUrlRef.current = previewUrl || "";
    setHeaderMediaPreviewUrl(previewUrl || "");
  };

  const handleHeaderMediaSourceChange = (source) => {
    setHeaderMediaSource(source);
    if (source !== HEADER_MEDIA_SOURCE.Upload) {
      replaceHeaderMediaPreview("");
    }
    setHeaderMediaUrl((current) => {
      if (!current) return "";
      const isUploadHandle = current.startsWith("handle:");
      if (source === HEADER_MEDIA_SOURCE.Url && isUploadHandle) return "";
      if (source === HEADER_MEDIA_SOURCE.Upload && !isUploadHandle) return "";
      return current;
    });
  };

  useEffect(() => {
    if (isOpen) {
      didRetargetNavigateRef.current = false;
      try {
        sessionStorage.removeItem("retarget_contactIds");
      } catch {}

      setName("");
      setTemplateQuery("");
      setShowTemplateSearch(false);
      setSelectedTemplate("");
      setSubmitAttempted(false);
      setHeaderMediaUrl("");
      setHeaderMediaSource(null);
      replaceHeaderMediaPreview("");
      setButtonParams([]);
      setRawButtonParams([]);
      setHeaderKind(HK.None);
      setRequiresHeaderMediaUrl(false);
      setScheduleMode("now");
      setScheduledAt("");
      setIsTemplatePreviewOpen(false);
      setSenders([]);
      setSelectedSenderId("");
      setAttributeDefinitions([]);

      if (businessId) {
        setLoadingTemplates(true);
        listAllApprovedCampaignTemplates(businessId, {
          sortKey: "createdAt",
          sortDir: "desc",
          pageSize: 200,
        })
          .then((res) => {
            if (res?.success) setTemplates(res.templates || []);
            else toast.error("Failed to load templates.");
          })
          .catch((err) => {
            console.error("Failed to fetch templates", err);
            toast.error("Error loading templates.");
          })
          .finally(() => setLoadingTemplates(false));

        setLoadingSenders(true);
        axiosClient
          .get(`WhatsAppSettings/senders/${businessId}`, { __silent: true })
          .then((r) => {
            const raw = Array.isArray(r.data) ? r.data : r.data?.items || [];
            const normalized = raw
              .map((x) => {
                const provider = normalizeMetaProvider(x.provider || x.Provider);
                const phoneNumberId = x.phoneNumberId ?? x.PhoneNumberId ?? "";
                const whatsAppNumber =
                  x.whatsAppBusinessNumber ??
                  x.whatsappBusinessNumber ??
                  x.displayNumber ??
                  x.phoneNumber ??
                  x.WhatsAppBusinessNumber ??
                  x.PhoneNumber ??
                  x.phoneNumberId ??
                  x.PhoneNumberId ??
                  "";

                const id = x.id ?? x.Id ?? `${provider}|${phoneNumberId}`;
                return { id, provider, phoneNumberId, whatsAppNumber };
              })
              .filter((x) => x.provider && x.phoneNumberId);

            setSenders(normalized);
            if (normalized.length === 1) setSelectedSenderId(normalized[0].id);
          })
          .catch(() => {
            setSenders([]);
          })
          .finally(() => setLoadingSenders(false));

        getCustomFieldDefinitions({ entityType: "Contact" })
          .then((definitions) => {
            setAttributeDefinitions(Array.isArray(definitions) ? definitions : []);
          })
          .catch((err) => {
            console.error("Failed to fetch attribute definitions", err);
            setAttributeDefinitions([]);
            toast.error("Failed to load contact field definitions.");
          });
      }
    }
  }, [isOpen, businessId]);

  useEffect(() => {
    return () => {
      if (headerMediaPreviewUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(headerMediaPreviewUrlRef.current);
      }
    };
  }, []);

  const campaignProvider = useMemo(
    () => pickProviderFromCampaignMeta(campaignMeta),
    [campaignMeta]
  );
  const campaignPhoneNumberId = useMemo(
    () => pickPhoneNumberIdFromCampaignMeta(campaignMeta),
    [campaignMeta]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (selectedSenderId) return;
    if (!campaignProvider || !campaignPhoneNumberId) return;

    const match = (senders || []).find(
      (s) =>
        s.provider === campaignProvider && s.phoneNumberId === campaignPhoneNumberId
    );
    setSelectedSenderId(match?.id || `${campaignProvider}|${campaignPhoneNumberId}`);
  }, [
    isOpen,
    selectedSenderId,
    campaignProvider,
    campaignPhoneNumberId,
    senders,
  ]);

  const selectedSender = useMemo(() => {
    if (!selectedSenderId) return null;
    return (senders || []).find((s) => s.id === selectedSenderId) || null;
  }, [senders, selectedSenderId]);

  const effectiveProvider = selectedSender?.provider || campaignProvider;
  const effectivePhoneNumberId =
    selectedSender?.phoneNumberId || campaignPhoneNumberId;

  const senderError = useMemo(() => {
    if (effectiveProvider && effectivePhoneNumberId) return null;
    return "Provider and PhoneNumberId are required. Please select a sender.";
  }, [effectiveProvider, effectivePhoneNumberId]);

  const handleSyncTemplates = async () => {
    if (!businessId) return;
    setSyncing(true);
    try {
      const res = await syncApprovedCampaignTemplates(businessId);
      if (res?.success) {
        toast.success("Templates synced!");
        const refreshed = await listAllApprovedCampaignTemplates(businessId, {
          sortKey: "createdAt",
          sortDir: "desc",
          pageSize: 200,
        });
        if (refreshed?.success) setTemplates(refreshed.templates || []);
      } else {
        toast.error("Sync failed.");
      }
    } catch {
      toast.error("Error syncing templates.");
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (!businessId || !selectedTemplate) {
      setTemplateDetails(null);
      setTemplateDetailsError(null);
      setTemplateDetailsLoading(false);
      return;
    }

    let alive = true;
    setTemplateDetailsLoading(true);
    setTemplateDetailsError(null);

    (async () => {
      try {
        const t = await getApprovedCampaignTemplateByName(
          businessId,
          selectedTemplate,
          {
            __silent: true,
            language: "en_US",
          }
        );
        if (alive && t) {
          setTemplateDetails(t);

          // Parse buttons
          const rb = t.buttonsJson ?? t.buttons ?? t.urlButtons ?? null;
          let parsed = [];
          if (Array.isArray(rb)) parsed = rb;
          else if (typeof rb === "string" && rb.trim().startsWith("[")) {
            try {
              parsed = JSON.parse(rb);
            } catch {
              parsed = [];
            }
          }
          setRawButtonParams(parsed);

          const hk = normalizeHeaderKind(t);
          setHeaderKind(hk);
          setRequiresHeaderMediaUrl(
            t.requiresHeaderMediaUrl === true ||
              t.RequiresMediaHeader === true ||
              isMediaHeader(hk)
          );

          // Dyn slots for buttons
          const dynSlots =
            parsed?.map((btn) => {
              const originalUrl =
                btn?.ParameterValue || btn?.parameterValue || "";
              const subtype = (
                btn?.SubType ||
                btn?.subType ||
                ""
              ).toLowerCase();
              const isDynamic =
                ["url", "copy_code", "flow"].includes(subtype) ||
                originalUrl.includes("{{1}}");
              return isDynamic ? "" : null;
            }) || [];
          setButtonParams(dynSlots);
        }
      } catch (err) {
        if (!alive) return;
        console.error("Failed to load template details", err);
        setTemplateDetails(null);
        setTemplateDetailsError("Failed to load template details.");
      } finally {
        if (alive) setTemplateDetailsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [isOpen, businessId, selectedTemplate]);

  const filteredTemplates = useMemo(() => {
    const q = String(templateQuery || "").trim().toLowerCase();
    if (!q) return templates;
    return (templates || []).filter((t) =>
      String(t?.name || t?.templateName || t?.id || "")
        .toLowerCase()
        .includes(q)
    );
  }, [templates, templateQuery]);

  const templateBodyText = useMemo(() => {
    if (selectedTemplate) {
      const t = extractTemplateBodyText(templateDetails);
      if (t) return t;
    }
    const fromList = (templates || []).find(
      (x) =>
        String(x?.name || x?.templateName || x?.id || "") ===
        String(selectedTemplate || "")
    );
    return extractTemplateBodyText(fromList);
  }, [templateDetails, templates, selectedTemplate]);

  const placeholderNumbers = useMemo(
    () => extractPlaceholdersFromText(templateBodyText),
    [templateBodyText]
  );

  const activeHeaderMediaSource = useMemo(() => {
    if (headerMediaSource) return headerMediaSource;
    if (headerMediaUrl.startsWith("handle:")) return HEADER_MEDIA_SOURCE.Upload;
    if (headerMediaUrl) return HEADER_MEDIA_SOURCE.Url;
    return null;
  }, [headerMediaSource, headerMediaUrl]);

  useEffect(() => {
    if (!isOpen) return;
    setVariableConfigs((prev) => {
      const next = {};
      for (const n of placeholderNumbers) {
        const k = String(n);
        next[k] = prev?.[k] ?? {
          source: "const",
          constValue: "",
          missingPolicy: "skip",
          fallbackValue: "",
        };
      }
      return next;
    });
  }, [isOpen, placeholderNumbers, setVariableConfigs]);

  const audienceSourceBucket = retargetAudience?.sourceBucket || "";
  const audienceSegmentLabel = retargetAudience?.segmentLabel || segmentLabel(audienceSourceBucket);
  const audienceResolvedCount = Number(retargetAudience?.resolvedCount || 0);
  const audienceSelectedCount = Number(retargetAudience?.selectedCount || 0);
  const usingSelectedSubset = retargetAudience?.usingSelectedSubset === true;
  const previewResolvedAt = retargetAudience?.previewResolvedAt || null;
  const finalContactIds = useMemo(
    () => (Array.isArray(retargetAudience?.contactIds) ? retargetAudience.contactIds : []),
    [retargetAudience]
  );
  const finalRecipientNumbers = useMemo(
    () =>
      Array.isArray(retargetAudience?.recipientPhones)
        ? retargetAudience.recipientPhones
        : [],
    [retargetAudience]
  );
  const selectedContactsList = useMemo(
    () =>
      Array.isArray(retargetAudience?.sampleRecipients)
        ? retargetAudience.sampleRecipients
        : [],
    [retargetAudience]
  );

  async function run() {
    if (!campaignId) return;
    setLoading(true);
    try {
      const msg =
        eligibilityError ||
        previewRequirementError ||
        nameError ||
        senderError ||
        templateError ||
        null;
      if (msg) {
        toast.warn(msg);
        return;
      }

      // Build button payload
      const buttonPayload =
        rawButtonParams?.map((btn, idx) => {
          const originalUrl = btn?.ParameterValue || btn?.parameterValue || "";
          const subtype = (btn?.SubType || btn?.subType || "").toLowerCase();
          const isDynamic =
            ["url", "copy_code", "flow"].includes(subtype) ||
            originalUrl.includes("{{1}}");

          return {
            text: btn?.Text || btn?.text || "Button",
            type: btn?.Type || btn?.type || "",
            value: isDynamic ? buttonParams[idx] || "" : originalUrl,
            position: idx + 1,
          };
        }) || [];

      const uniq = (xs) => [...new Set((xs || []).map((x) => String(x).trim()).filter(Boolean))];
      const normalizedContactIds = uniq(finalContactIds);
      const normalizedRecipientNumbers = uniq(finalRecipientNumbers);

      if (!normalizedContactIds.length && !normalizedRecipientNumbers.length) {
        toast.warn("Resolve the retarget audience on the report page before launching.");
        return;
      }

      const placeholders = extractPlaceholdersFromText(
        extractTemplateBodyText(templateDetails)
      );

      const templateVariableConfigs = {};
      for (const n of placeholders) {
        const cfg = variableConfigs?.[String(n)] || {};
        templateVariableConfigs[String(n)] = {
          source: String(cfg.source || "const"),
          constValue: String(cfg.constValue ?? ""),
          missingPolicy: String(cfg.missingPolicy || "skip"),
          fallbackValue: String(cfg.fallbackValue ?? ""),
        };
      }

      const baseContact = selectedContactsList[0] || null;
      const templateParameters = placeholders.map((n) => {
        const r = resolveVariableValue(baseContact, n);
        return String(r?.value ?? "");
      });

      const templateParametersMap = {};
      placeholders.forEach((n, idx) => {
        templateParametersMap[`{{${n}}}`] = templateParameters[idx] ?? "";
      });

      const messageTemplate = extractTemplateBodyText(templateDetails);
      const templateLanguage = templateDetails?.language || "en_US";
      const scheduledAtIso =
        scheduleMode === "later" && scheduledAt
          ? new Date(scheduledAt).toISOString()
          : null;
      const campaignType =
        headerKind === HK.Image ? "image" : headerKind === HK.Video ? "video" : "text";

      const providerUpper = normalizeMetaProvider(effectiveProvider) || "META_CLOUD";
      const phoneNumberIdValue = String(effectivePhoneNumberId || "").trim();
      const createdByValue =
        localStorage.getItem("userId") || localStorage.getItem("createdBy") || "";
      const businessIdValue =
        businessId || localStorage.getItem("businessId") || "";

      const res = await axiosClient.post("campaigns/retarget", {
        // support both newer and legacy backends
        campaignId,
        sourceCampaignId: campaignId,
        bucket: audienceSourceBucket,
        sourceBucket: audienceSourceBucket,
        createMode: "DRAFT_CAMPAIGN",
        name: name?.trim() || null,
        contactIds: normalizedContactIds,
        recipientPhones: normalizedRecipientNumbers,
        recipientNumbers: normalizedRecipientNumbers,
        recipients: normalizedRecipientNumbers,
        phones: normalizedRecipientNumbers,
        deduplicate: true,

        // Flat campaign shape (some backends expect this)
        businessId: businessIdValue,
        createdBy: createdByValue,
        messageTemplate,
        templateId: selectedTemplate,
        templateName: selectedTemplate,
        templateLanguage,
        headerKind,
        headerMediaUrl: isMediaHeader(headerKind) ? headerMediaUrl : null,
        imageUrl: headerKind === HK.Image ? headerMediaUrl : null,
        videoUrl: headerKind === HK.Video ? headerMediaUrl : null,
        documentUrl: headerKind === HK.Document ? headerMediaUrl : null,
        buttonParams: buttonPayload,
        templateParameters,
        templateParametersMap,
        templateVariableConfigs,
        provider: providerUpper,
        phoneNumberId: phoneNumberIdValue,
        scheduledAt: scheduledAtIso,
        campaignType,
        useCsvPersonalization: false,

        // New params nested within campaign for the backend DTO
        campaign: {
          name: name?.trim() || `Retarget: ${campaignMeta?.name ?? "Campaign"}`,
          businessId: businessIdValue,
          createdBy: createdByValue,
          messageTemplate,
          templateId: selectedTemplate,
          templateName: selectedTemplate,
          templateLanguage,
          headerKind: headerKind,
          headerMediaUrl: isMediaHeader(headerKind) ? headerMediaUrl : null,
          imageUrl: headerKind === HK.Image ? headerMediaUrl : null,
          videoUrl: headerKind === HK.Video ? headerMediaUrl : null,
          documentUrl: headerKind === HK.Document ? headerMediaUrl : null,
          buttonParams: buttonPayload,
          multiButtons: buttonPayload.map(b => ({
            buttonText: b.text,
            buttonType: b.type,
            targetUrl: b.value,
            position: b.position
          })),
          templateParameters,
          templateParametersMap,
          templateVariableConfigs,
          provider: providerUpper,
          phoneNumberId: phoneNumberIdValue,
          scheduledAt: scheduledAtIso,
          campaignType,
          useCsvPersonalization: false,
        },
      }, { __silent: true });

      const data = res?.data?.data ?? res?.data ?? null;
      toast.success("Retarget wizard completed.");

      if (data?.newCampaignId) {
        didRetargetNavigateRef.current = true;
        onClose();
        navigate("/app/campaigns/template-campaigns-list", { replace: true });
        return;
      }

      if (Array.isArray(data?.contactIds)) {
        // MVP: ignore contactIds mode (backend fallback).
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn(
            "[Retarget] Backend returned contactIds; MVP ignores this mode."
          );
        }
      }

      onClose();
    } catch (err) {
      console.error(err);
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.error("[Retarget] Response:", err?.response?.data);
      }
      const data = err?.response?.data;
      const msg =
        data?.message ||
        data?.error ||
        data?.title ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        "Failed to run retarget wizard.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  const eligibilityError = useMemo(() => {
    const r = getRetargetEligibility(campaignStatus);
    return r.ok ? null : r.reason;
  }, [campaignStatus]);

  const previewRequirementError = useMemo(() => {
    if (!retargetAudience?.isReady) {
      return "Resolve the retarget audience on the report page before launching.";
    }
    if (audienceResolvedCount > 0) return null;
    return "No recipients matched the current retarget filters.";
  }, [audienceResolvedCount, retargetAudience]);

  const nameError = useMemo(() => {
    if (name?.trim()) return null;
    return "Campaign name is required.";
  }, [name]);

  const templateError = useMemo(() => {
    if (!selectedTemplate) return "Template is required.";
    if (templateDetailsLoading) return "Loading template details...";
    if (templateDetailsError) return templateDetailsError;
    return null;
  }, [selectedTemplate, templateDetailsLoading, templateDetailsError]);

  const firstValidationError =
    eligibilityError ||
    previewRequirementError ||
    nameError ||
    senderError ||
    templateError ||
    null;

  const canLaunch =
    !loading &&
    !eligibilityError &&
    !previewRequirementError &&
    !nameError &&
    !senderError &&
    !templateError;

  const uiLocked = !!eligibilityError || loading;

  const contactsReady = selectedContactsList.length > 0;

  const customFieldKeys = useMemo(() => {
    const keys = new Set();
    for (const definition of attributeDefinitions || []) {
      const key = String(definition?.key || "").trim();
      if (key) keys.add(key);
    }
    return [...keys].sort((a, b) => a.localeCompare(b));
  }, [attributeDefinitions]);

  const resolveVariableValue = useCallback((contact, n) => {
    const cfg = variableConfigs?.[String(n)];
    if (!cfg) return { value: "", missing: true };

    const source = String(cfg.source || "const");
    if (source === "const") {
      const v = String(cfg.constValue ?? "");
      return { value: v, missing: !v.trim() };
    }

    let raw = "";
    if (source === "contact:name") raw = getContactNameValue(contact);
    else if (source === "contact:phone") raw = getContactPhoneValue(contact);
    else if (source.startsWith("custom:")) {
      raw = getCustomFieldValue(contact, source.slice("custom:".length));
    }

    const v = String(raw ?? "");
    if (v.trim()) return { value: v, missing: false };

    if (String(cfg.missingPolicy || "skip") === "fallback") {
      const fb = String(cfg.fallbackValue ?? "");
      if (fb.trim()) return { value: fb, missing: false };
    }

    return { value: v, missing: true };
  }, [variableConfigs]);

  const templatePreviewDraft = useMemo(() => {
    if (!selectedTemplate) return null;
    const hk = String(headerKind || HK.None).toUpperCase();
    const previewHeaderType =
      hk === "IMAGE" || hk === "VIDEO" || hk === "DOCUMENT" || hk === "TEXT"
        ? hk
        : "NONE";

    const examples = placeholderNumbers.map((n) => {
      const r = resolveVariableValue(selectedContactsList[0] || null, n);
      const value = String(r?.value ?? "").trim();
      return value || `Value ${n}`;
    });

    return {
      name: selectedTemplate,
      language:
        templateDetails?.language ||
        templateDetails?.languageCode ||
        "en_US",
      headerType: previewHeaderType,
      headerText: String(
        templateDetails?.headerText || templateDetails?.HeaderText || ""
      ),
      headerMediaUrl: requiresHeaderMediaUrl
        ? headerMediaPreviewUrl || headerMediaUrl
        : null,
      bodyText: String(templateBodyText || ""),
      footerText: String(
        templateDetails?.footerText || templateDetails?.FooterText || ""
      ),
      buttons: normalizeButtonsForTemplatePreview(rawButtonParams, buttonParams),
      examples,
    };
  }, [
    selectedTemplate,
    headerKind,
    placeholderNumbers,
    resolveVariableValue,
    selectedContactsList,
    templateDetails,
    requiresHeaderMediaUrl,
    headerMediaUrl,
    headerMediaPreviewUrl,
    templateBodyText,
    rawButtonParams,
    buttonParams,
  ]);

  function handleRunClick() {
    setSubmitAttempted(true);
    if (!canLaunch) {
      if (firstValidationError) toast.warn(firstValidationError);
      return;
    }
    run();
  }

  function handleOpenChange(nextOpen) {
    if (nextOpen) return;
    if (loading) return;
      const isDirty =
      !!name?.trim() ||
      !!selectedTemplate ||
      !!templateQuery.trim();

    if (isDirty) {
      const ok = window.confirm("Discard changes and close?");
      if (!ok) return;
    }

    if (!didRetargetNavigateRef.current) {
      try {
        sessionStorage.removeItem("retarget_contactIds");
      } catch {}
    }
    onClose();
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        aria-describedby={undefined}
        className={`!max-w-4xl p-0 border-none shadow-2xl rounded-lg flex flex-col max-h-[90vh] overflow-hidden ${buttonTypographyClass}`}
      >
        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 bg-white shrink-0">
          <DialogHeader className="p-0 mb-0 border-none space-y-1">
            <DialogTitle className="text-xl font-bold text-slate-800 tracking-tight">
              Retarget wizard
            </DialogTitle>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 font-semibold text-slate-600">
                {audienceSegmentLabel}
              </span>
              <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                {usingSelectedSubset
                  ? `${audienceSelectedCount.toLocaleString()} selected`
                  : `${audienceResolvedCount.toLocaleString()} resolved`}
              </span>
            </div>
          </DialogHeader>
          <DialogClose asChild>
            <button className="text-slate-400 hover:text-slate-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </DialogClose>
        </div>

        <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto custom-scrollbar">
          {eligibilityError ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-xl bg-amber-100 p-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-amber-900">
                    Retargeting is disabled
                  </div>
                  <div className="mt-1 text-xs text-amber-800">
                    {eligibilityError}
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-x-5 gap-y-2">
            <div className="space-y-1">
              <label className="text-[13px] font-bold text-slate-800">
                Audience
              </label>
              <p className="text-[11px] text-slate-500 leading-tight">
                Review the resolved audience from the report page before launching.
              </p>
            </div>
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Segment
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {audienceSegmentLabel}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Audience size
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {audienceResolvedCount.toLocaleString()} resolved
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Launch mode
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {usingSelectedSubset
                      ? `${audienceSelectedCount.toLocaleString()} selected recipients`
                      : "All resolved recipients"}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-xs text-slate-600">
                <div>
                  {retargetAudience?.attributeSummary || "No Attribute narrowing applied."}
                </div>
                {previewResolvedAt ? (
                  <span className="font-medium text-slate-500">
                    Refreshed {formatDateTime(previewResolvedAt)}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-x-5 gap-y-2">
            <div className="space-y-1">
              <label className="text-[13px] font-bold text-slate-800">
                Campaign Name
              </label>
              <p className="text-[11px] text-slate-500 leading-tight">
                Identifies this retarget run.
              </p>
            </div>
            <div className="space-y-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Retarget - Delivered not read"
                disabled={uiLocked}
                className="w-full bg-slate-100 border-none rounded-xl px-4 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
              />
              {submitAttempted && nameError ? (
                <div className="text-[11px] font-semibold text-rose-600">
                  {nameError}
                </div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-x-5 gap-y-2">
            <div className="space-y-1">
              <label className="text-[13px] font-bold text-slate-800">
                Sender <span className="text-rose-500">*</span>
              </label>
              <p className="text-[11px] text-slate-500 leading-tight">
                WhatsApp number used to send.
              </p>
            </div>
            <div className="space-y-2">
              <select
                value={selectedSenderId}
                onChange={(e) => setSelectedSenderId(e.target.value)}
                disabled={loadingSenders || uiLocked}
                className="w-full bg-slate-100 border-none rounded-xl px-4 py-2 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20 appearance-none cursor-pointer disabled:opacity-60"
              >
                <option value="">
                  {loadingSenders
                    ? "Loading..."
                    : senders.length
                    ? "Select sender"
                    : "No senders found"}
                </option>
                {senders.map((s) => (
                  <option key={s.id} value={s.id}>
                    {(s.whatsAppNumber || s.phoneNumberId || "Sender").toString()} (
                    {s.provider})
                  </option>
                ))}
                {!senders.find((s) => s.id === `${campaignProvider}|${campaignPhoneNumberId}`) &&
                campaignProvider &&
                campaignPhoneNumberId ? (
                  <option value={`${campaignProvider}|${campaignPhoneNumberId}`}>
                    {campaignPhoneNumberId} ({campaignProvider})
                  </option>
                ) : null}
              </select>
              {submitAttempted && senderError ? (
                <div className="text-[11px] font-semibold text-rose-600">
                  {senderError}
                </div>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-x-5 gap-y-2">
            <div className="space-y-1">
              <label className="text-[13px] font-bold text-slate-800">
                Template Name
              </label>
              <p className="text-[11px] text-slate-500 leading-tight">
                Approved WhatsApp template.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 min-w-0 relative">
                {showTemplateSearch && (
                  <div className="w-[180px] flex items-center gap-2 bg-slate-100 rounded-xl px-3 py-1.5 animate-in fade-in slide-in-from-right-2 duration-300 relative z-20">
                    <Search className="w-3.5 h-3.5 text-slate-400" />
                    <input
                      autoFocus
                      value={templateQuery}
                      onChange={(e) => setTemplateQuery(e.target.value)}
                      placeholder="Search..."
                      className="w-full bg-transparent border-none py-0.5 text-sm text-slate-800 outline-none placeholder:text-slate-400"
                    />
                    <button
                      onClick={() => {
                        setShowTemplateSearch(false);
                        setTemplateQuery("");
                      }}
                      className="p-1 hover:bg-slate-200 rounded-md text-slate-500 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>

                    {/* Auto-show Results List */}
                    {templateQuery.trim() && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-30 max-h-[250px] overflow-y-auto custom-scrollbar animate-in zoom-in-95 duration-200">
                        {filteredTemplates.length > 0 ? (
                          filteredTemplates.map((t) => (
                            <button
                              key={t.id || t.name}
                              type="button"
                              onClick={() => {
                                setHeaderMediaUrl("");
                                setHeaderMediaSource(null);
                                replaceHeaderMediaPreview("");
                                setSelectedTemplate(t.name);
                                setShowTemplateSearch(false);
                                setTemplateQuery("");
                              }}
                              className="w-full text-left px-4 py-2.5 text-[13px] font-medium text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors border-b border-slate-50 last:border-none"
                            >
                              {t.name}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-[12px] text-slate-400 italic">
                            No templates found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex-1 relative">
                  <select
                    value={selectedTemplate}
                    disabled={loadingTemplates || uiLocked}
                    onChange={(e) => {
                      setHeaderMediaUrl("");
                      setHeaderMediaSource(null);
                      replaceHeaderMediaPreview("");
                      setSelectedTemplate(e.target.value);
                    }}
                    className="w-full bg-slate-100 border-none rounded-xl px-4 py-2 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20 appearance-none cursor-pointer disabled:opacity-60"
                  >
                    <option value="">
                      {loadingTemplates ? "Loading..." : "Select template"}
                    </option>
                    {filteredTemplates.map((t) => (
                      <option key={t.id || t.name} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-4 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>

              {!showTemplateSearch && (
                <button
                  type="button"
                  onClick={() => setShowTemplateSearch(true)}
                  disabled={uiLocked}
                  className="flex items-center justify-center h-[40px] w-[40px] rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-emerald-600 transition-all shadow-sm"
                  title="Search Templates"
                >
                  <Search className="h-4 w-4" />
                </button>
              )}

              <button
                type="button"
                onClick={handleSyncTemplates}
                disabled={uiLocked || syncing || loadingTemplates}
                className="flex items-center justify-center h-[40px] w-[40px] rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-emerald-600 transition-all disabled:opacity-50 shadow-sm"
                title="Sync Templates from Meta"
              >
                <RefreshCcw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
              </button>
              <button
                type="button"
                onClick={() => setIsTemplatePreviewOpen(true)}
                disabled={uiLocked || !selectedTemplate}
                className="flex items-center justify-center h-[40px] w-[40px] rounded-lg bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-emerald-600 transition-all disabled:opacity-50 shadow-sm"
                title="Preview selected template"
              >
                <Eye className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1">
              <span className="font-medium">
                {templateQuery ? `Filtered: ${filteredTemplates.length}` : `Total: ${templates.length}`}
              </span>
              {submitAttempted && templateError ? (
                <span className="font-bold text-rose-600">
                  {templateError}
                </span>
              ) : null}
            </div>

            {/* Template selector ends here */}
          </div>

          {selectedTemplate ? (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 border-b border-emerald-100 bg-gradient-to-r from-emerald-50/80 to-white">
              <div className="flex items-start gap-3">
                <div className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <Wand2 className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-[13px] font-bold text-slate-800 uppercase tracking-tight">
                    Template Personalization
                  </div>
                  <div className="mt-0.5 text-[12px] text-slate-600">
                    Configure template variables, media, and button parameters.
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4">
            {templateDetailsLoading ? (
              <div className="mt-1 text-[12px] text-slate-600 font-medium flex items-center justify-center gap-2 py-6 bg-slate-50 rounded-xl border border-slate-100">
                <RefreshCcw className="w-4 h-4 animate-spin text-emerald-500" />
                Loading template details...
              </div>
            ) : templateDetailsError ? (
              <div className="mt-1 text-[12px] font-semibold text-rose-600 bg-rose-50 rounded-xl p-4 border border-rose-100">
                {templateDetailsError}
              </div>
            ) : (
              <div className="mt-1 space-y-5">

                {/* 1. Header Media */}
                {requiresHeaderMediaUrl && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-300">
                    <div className="flex items-center gap-2">
                       <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                       <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                         Header Content
                       </h4>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3.5">
                      <label className="block text-[11px] font-bold text-slate-600 mb-2">
                        {mediaLabel(headerKind)} <span className="text-rose-500">*</span>
                      </label>
                      <div className="space-y-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() =>
                              handleHeaderMediaSourceChange(HEADER_MEDIA_SOURCE.Url)
                            }
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                              activeHeaderMediaSource === HEADER_MEDIA_SOURCE.Url
                                ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:text-emerald-700"
                            }`}
                          >
                            Use HTTPS URL
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleHeaderMediaSourceChange(HEADER_MEDIA_SOURCE.Upload)
                            }
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                              activeHeaderMediaSource === HEADER_MEDIA_SOURCE.Upload
                                ? "border-emerald-600 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200 hover:text-emerald-700"
                            }`}
                          >
                            Upload File
                          </button>
                        </div>

                        {activeHeaderMediaSource === HEADER_MEDIA_SOURCE.Url ? (
                          <>
                            <input
                              type="url"
                              value={
                                headerMediaUrl.startsWith("handle:")
                                  ? ""
                                  : headerMediaUrl
                              }
                              onChange={(e) => {
                                setHeaderMediaSource(HEADER_MEDIA_SOURCE.Url);
                                replaceHeaderMediaPreview("");
                                setHeaderMediaUrl(e.target.value);
                              }}
                              placeholder={`https://example.com/media.${headerKind === HK.Image ? "jpg" : headerKind === HK.Video ? "mp4" : "pdf"}`}
                              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20 shadow-sm"
                            />
                            <p className="mt-1.5 text-[10px] text-slate-500 leading-normal">
                              Provide a direct public link to the {headerKind} file.
                            </p>
                          </>
                        ) : null}

                        {activeHeaderMediaSource === HEADER_MEDIA_SOURCE.Upload ? (
                          <>
                            <StandaloneMediaUploader
                              mediaType={String(headerKind || HK.Image).toUpperCase()}
                              handle={headerMediaUrl}
                              onPreview={(previewUrl) =>
                                replaceHeaderMediaPreview(previewUrl)
                              }
                              onUploaded={(handle) => {
                                setHeaderMediaSource(HEADER_MEDIA_SOURCE.Upload);
                                setHeaderMediaUrl(handle);
                              }}
                            />
                            {headerMediaUrl.startsWith("handle:") ? (
                              <button
                                type="button"
                                onClick={() => {
                                  replaceHeaderMediaPreview("");
                                  setHeaderMediaUrl("");
                                }}
                                className="text-[10px] text-rose-500 hover:text-rose-700 font-medium flex items-center gap-1"
                              >
                                <X className="h-3 w-3" />
                                Remove uploaded media
                              </button>
                            ) : null}
                          </>
                        ) : null}

                        {!activeHeaderMediaSource ? (
                          <p className="text-[10px] text-slate-500 leading-normal">
                            Choose whether you want to provide a direct HTTPS URL or
                            upload the {headerKind} file.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. Dynamic Buttons */}
                {buttonParams.some((p) => p !== null) && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-300">
                    <div className="flex items-center gap-2">
                       <div className="w-1.5 h-6 bg-blue-500 rounded-full" />
                       <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                         Interactive Buttons
                       </h4>
                    </div>
                    <div className="rounded-xl bg-slate-50 border border-slate-100 p-3.5 space-y-4">
                      {rawButtonParams.map((btn, idx) => {
                        if (buttonParams[idx] === null) return null;
                        return (
                          <div key={idx} className="space-y-1.5">
                            <label className="block text-[11px] font-bold text-slate-600">
                              {btn.text || btn.Text} <span className="text-[10px] font-normal text-slate-400">({btn.type || btn.Type})</span>
                            </label>
                            <input
                              value={buttonParams[idx]}
                              onChange={(e) => {
                                const next = [...buttonParams];
                                next[idx] = e.target.value;
                                setButtonParams(next);
                              }}
                              placeholder="Enter dynamic value or URL"
                              className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500/20 shadow-sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 3. Body Variables */}
                {placeholderNumbers.length > 0 && (
                  <div className="space-y-3 animate-in fade-in slide-in-from-top-1 duration-300">
                    <div className="flex items-center gap-2">
                       <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                       <h4 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
                         Message Variables
                       </h4>
                    </div>
                    
                    <div className="grid grid-cols-[120px,1fr] gap-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 px-1">
                      <div>Placeholder</div>
                      <div>Mapping Configuration</div>
                    </div>

                    <div className="space-y-3">
                  {placeholderNumbers.map((n) => {
                    const cfg = variableConfigs?.[String(n)] || {};
                    const source = String(cfg.source || "const");
                    const isConst = source === "const";
                    const missingPolicy = String(cfg.missingPolicy || "skip");

                    const setCfg = (patch) =>
                      setVariableConfigs((prev) => ({
                        ...(prev || {}),
                        [String(n)]: {
                          ...(prev?.[String(n)] || {
                            source: "const",
                            constValue: "",
                            missingPolicy: "skip",
                            fallbackValue: "",
                          }),
                          ...patch,
                        },
                      }));

                    return (
                      <div
                        key={n}
                        className="grid grid-cols-[120px,1fr] gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3"
                      >
                        <div className="flex items-center gap-2">
                          <span className="rounded-lg bg-white px-2 py-1 font-mono text-[12px] font-semibold text-slate-800 border border-slate-200">
                            {`{{${n}}}`}
                          </span>
                        </div>

                        <div className="space-y-2">
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <select
                              value={source}
                              onChange={(e) =>
                                setCfg({
                                  source: e.target.value,
                                })
                              }
                              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                            >
                              <option value="const">Constant</option>
                              {contactsReady ? (
                                <>
                                  <option value="contact:name">
                                    Contact field: Name
                                  </option>
                                  <option value="contact:phone">
                                    Contact field: Phone
                                  </option>
                                  {customFieldKeys.length ? (
                                    <optgroup label="Custom fields">
                                      {customFieldKeys.map((k) => (
                                        <option
                                          key={k}
                                          value={`custom:${k}`}
                                        >
                                          Custom field: {k}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ) : null}
                                </>
                              ) : null}
                            </select>

                            {isConst ? (
                              <input
                                value={String(cfg.constValue ?? "")}
                                onChange={(e) =>
                                  setCfg({ constValue: e.target.value })
                                }
                                placeholder="Constant value"
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              />
                            ) : (
                              <select
                                value={missingPolicy}
                                onChange={(e) =>
                                  setCfg({ missingPolicy: e.target.value })
                                }
                                className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              >
                                <option value="skip">Skip missing</option>
                                <option value="fallback">
                                  Use fallback constant
                                </option>
                              </select>
                            )}
                          </div>

                          {!isConst && missingPolicy === "fallback" ? (
                            <input
                              value={String(cfg.fallbackValue ?? "")}
                              onChange={(e) =>
                                setCfg({ fallbackValue: e.target.value })
                              }
                              placeholder="Fallback constant (when contact value is missing)"
                              className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                            />
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
                  </div>
                )}

              </div>
            )}
            </div>
          </div>
          ) : null}




          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-x-5 gap-y-2">
            <div className="space-y-1">
              <label className="text-[13px] font-bold text-slate-800">
                Scheduling
              </label>
              <p className="text-[11px] text-slate-500 leading-tight">
                When should this campaign run?
              </p>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label
                  className={cx(
                    "relative flex cursor-pointer rounded-xl border p-3 transition-all",
                    scheduleMode === "now"
                      ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600"
                      : "border-slate-200 bg-white hover:bg-slate-50",
                    uiLocked ? "pointer-events-none opacity-60" : ""
                  )}
                >
                  <input
                    type="radio"
                    name="schedule"
                    value="now"
                    className="sr-only"
                    checked={scheduleMode === "now"}
                    onChange={() => setScheduleMode("now")}
                    disabled={uiLocked}
                  />
                  <div className="flex flex-1 flex-col">
                    <span className="text-sm font-bold text-slate-900">
                      Send Immediately
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Launch as soon as created.
                    </span>
                  </div>
                  <Send
                    className={cx(
                      "h-4 w-4",
                      scheduleMode === "now" ? "text-emerald-600" : "text-slate-300"
                    )}
                  />
                </label>

                <label
                  className={cx(
                    "relative flex cursor-pointer rounded-xl border p-3 transition-all",
                    scheduleMode === "later"
                      ? "border-emerald-600 bg-emerald-50 ring-1 ring-emerald-600"
                      : "border-slate-200 bg-white hover:bg-slate-50",
                    uiLocked ? "pointer-events-none opacity-60" : ""
                  )}
                >
                  <input
                    type="radio"
                    name="schedule"
                    value="later"
                    className="sr-only"
                    checked={scheduleMode === "later"}
                    onChange={() => setScheduleMode("later")}
                    disabled={uiLocked}
                  />
                  <div className="flex flex-1 flex-col">
                    <span className="text-sm font-bold text-slate-900">
                      Schedule for Later
                    </span>
                    <span className="text-[11px] text-slate-500">
                      Pick a future time.
                    </span>
                  </div>
                  <Calendar
                    className={cx(
                      "h-4 w-4",
                      scheduleMode === "later" ? "text-emerald-600" : "text-slate-300"
                    )}
                  />
                </label>
              </div>

              {scheduleMode === "later" && (
                <div className="mt-2 animate-in slide-in-from-top-1 duration-200">
                  <input
                    type="datetime-local"
                    className="w-full bg-slate-100 border-none rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                    value={scheduledAt}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    min={new Date().toISOString().slice(0, 16)}
                    disabled={uiLocked}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[180px,1fr] gap-x-5 gap-y-2 items-center pt-1">
            <div className="space-y-1">
              <label className="text-[13px] font-bold text-slate-800">
                Test Campaign
              </label>
              <p className="text-[11px] text-slate-500 leading-tight">
                Send a quick test.
              </p>
            </div>
            <div>
              <button
                type="button"
                onClick={() => toast.info("Test message is coming soon.")}
                disabled={uiLocked || !selectedTemplate}
                className="inline-flex items-center gap-2 px-6 py-2 border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Send Test Message
              </button>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 bg-white border-t border-slate-100 mt-0 shrink-0">
          <div className="w-full space-y-2">

            {submitAttempted && firstValidationError ? (
              <div className="text-[12px] font-semibold text-rose-600">
                {firstValidationError}
              </div>
            ) : null}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-50"
              >
                Close
              </button>
              <button
                onClick={handleRunClick}
                disabled={loading || !retargetAudience?.isReady}
                aria-disabled={!canLaunch}
                className={cx(
                  "flex-[2] inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-xl shadow-emerald-600/20 hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:active:scale-100",
                  !canLaunch && retargetAudience?.isReady ? "opacity-50 cursor-not-allowed" : ""
                )}
              >
                {loading ? (
                  <RefreshCcw className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {loading
                  ? "Processing..."
                  : scheduleMode === "now"
                  ? "Launch Campaign"
                  : "Schedule Campaign"}
              </button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      <Dialog open={isTemplatePreviewOpen} onOpenChange={setIsTemplatePreviewOpen}>
        <DialogContent className={`max-w-[380px] p-0 overflow-hidden !border-none !bg-transparent !shadow-none [&>button]:hidden ${buttonTypographyClass}`}>
          {templatePreviewDraft ? (
            <div className="relative flex w-full flex-col items-center">
              <button
                type="button"
                onClick={() => setIsTemplatePreviewOpen(false)}
                className="absolute right-3 top-3 z-20 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors focus:outline-none"
                title="Close Preview"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="w-full">
                <WhatsAppTemplatePreview draft={templatePreviewDraft} />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function KV({ label, value, copyable }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    if (!value || !copyable) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.info("Copied to clipboard", { autoClose: 1000 });
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 group/kv">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <div
        onClick={handleCopy}
        className={cx(
          "text-xs font-semibold text-slate-900 text-right transition-all",
          copyable
            ? "cursor-pointer hover:text-emerald-600 font-mono bg-slate-50 px-1.5 py-0.5 rounded border border-transparent hover:border-emerald-200 hover:bg-emerald-50"
            : ""
        )}
      >
        {value ?? "-"}
        {copyable && (
          <span className="ml-1.5 opacity-0 group-hover/kv:opacity-100 transition-opacity text-[10px] text-emerald-500 font-bold uppercase">
            {copied ? "Copied!" : "Copy"}
          </span>
        )}
      </div>
    </div>
  );
}

function Skeleton({ className }) {
  return (
    <div className={cx("animate-pulse bg-slate-200 rounded-xl", className)} />
  );
}

function StatCardSkeleton() {
  return (
    <div className="flex h-full w-full items-center gap-2.5 rounded-[8px] border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
      <Skeleton className="h-9 w-9 rounded-full" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
    </div>
  );
}

function ProgressRing({
  percentage,
  size = 32,
  strokeWidth = 3,
  colorClass = "text-emerald-500",
  children,
}) {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  return (
    <div
      className="relative inline-flex items-center justify-center shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          className="text-slate-100"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={cx("transition-all duration-500 ease-out", colorClass)}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          style={{ strokeDashoffset: offset }}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div className="absolute flex items-center justify-center">
        {children}
      </div>
    </div>
  );
}

function StatCard({
  title,
  percentage,
  count,
  icon: Icon,
  iconClassName,
  ringColor,
  active,
  onClick,
  helpText,
}) {
  const interactive = typeof onClick === "function";
  const Container = interactive ? "button" : "div";

  return (
    <Container
      type={interactive ? "button" : undefined}
      onClick={onClick}
      title={helpText}
      aria-pressed={interactive ? Boolean(active) : undefined}
      className={cx(
        "group relative flex h-full min-h-[80px] w-full items-center gap-2.5 overflow-hidden rounded-[8px] border px-3 py-2.5 text-left transition-all duration-300",
        active
          ? "border-emerald-500 bg-white shadow-sm"
          : "border-slate-200 bg-white shadow-sm",
        interactive
          ? "cursor-pointer hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-200"
          : "cursor-default"
      )}
    >
      <div className="shrink-0 transition-transform duration-300 group-hover:scale-[1.04]">
        {Icon ? (
          <ProgressRing
            percentage={percentage || 0}
            colorClass={ringColor}
            size={36}
            strokeWidth={3}
          >
            <div
              className={cx(
                "rounded-full p-1.5 transition-all duration-300",
                active
                  ? iconClassName
                  : "bg-slate-50 text-slate-400 grayscale-[0.25]"
              )}
            >
              <Icon className="h-3 w-3" />
            </div>
          </ProgressRing>
        ) : null}
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={cx(
            "truncate text-[12px] font-medium leading-none",
            active ? "text-emerald-900" : "text-slate-500"
          )}
        >
          {title}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-[14px] font-semibold leading-none tracking-tight text-slate-950">
            {percentage != null ? `${percentage.toFixed(0)}%` : "-"}
          </span>
          <span
            className={cx(
              "inline-flex items-center rounded-full border px-2 py-1 text-[11px] font-semibold leading-none",
              active
                ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                : "border-slate-200 bg-slate-50 text-slate-600"
            )}
          >
            {typeof count === "number" ? count.toLocaleString() : count || "0"}
          </span>
        </div>
      </div>
    </Container>
  );
}

export default function CampaignLogReportPage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const { can, isLoading, businessId } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const allowed = useMemo(() => {
    return (
      can?.(FK.CAMPAIGN_REPORT_LOGS_VIEW) ||
      can?.(FK.CAMPAIGN_STATUS_VIEW) ||
      can?.(FK.CAMPAIGN_LIST_VIEW)
    );
  }, [can]);

  const tabFromUrl = (searchParams.get("tab") || "").toLowerCase();
  const windowDaysFromUrl = parseWindowDaysParam(searchParams.get("windowDays"));

  const [activeTab, setActiveTab] = useState(
    STAT_TABS.includes(tabFromUrl) ? tabFromUrl : "recipients"
  );
  const [windowDays, setWindowDays] = useState(
    windowDaysFromUrl
  );

  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [campaignMeta, setCampaignMeta] = useState(null);

  const [audienceSegment, setAudienceSegment] = useState(
    () => SEGMENT_FROM_TAB[tabFromUrl] || ALL_RECIPIENTS_SEGMENT
  );
  const [audienceSearch, setAudienceSearch] = useState("");
  const [audiencePage, setAudiencePage] = useState(1);
  const [audienceItems, setAudienceItems] = useState([]);
  const [audienceTotalCount, setAudienceTotalCount] = useState(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceHasLoaded, setAudienceHasLoaded] = useState(false);
  const [audienceError, setAudienceError] = useState(null);
  const [audienceSource, setAudienceSource] = useState(null); // 'audience' | 'bucket-contacts' | 'campaign-logs' | 'recipients' | 'mock'
  const [audienceExporting, setAudienceExporting] = useState(false);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [isRetargetModalOpen, setIsRetargetModalOpen] = useState(false);
  const [attributeDefinitions, setAttributeDefinitions] = useState([]);
  const [loadingAttributeDefinitions, setLoadingAttributeDefinitions] = useState(false);
  const [attributeConditions, setAttributeConditions] = useState([]);
  const [isAttributesOpen, setIsAttributesOpen] = useState(false);
  const [retargetPreviewResult, setRetargetPreviewResult] = useState(null);
  const [retargetPreviewLoading, setRetargetPreviewLoading] = useState(false);
  const [retargetPreviewError, setRetargetPreviewError] = useState(null);
  const [retargetPreviewResolvedAt, setRetargetPreviewResolvedAt] = useState(null);

  const audienceSectionRef = useRef(null);
  const selectedAudienceRowsRef = useRef(new Map());
  const audienceAppliedRef = useRef({
    segment: null,
    search: null,
    windowDays: null,
  });
  const audienceTotalPages =
    audienceTotalCount != null
      ? Math.max(1, Math.ceil(Number(audienceTotalCount) / AUDIENCE_PAGE_SIZE))
      : null;

  function setUrlState(next) {
    const sp = new URLSearchParams(searchParams);
    if (next.tab) sp.set("tab", next.tab);
    if (Object.prototype.hasOwnProperty.call(next, "windowDays")) {
      if (next.windowDays == null) sp.set("windowDays", "all");
      else sp.set("windowDays", String(next.windowDays));
    }
    setSearchParams(sp, { replace: true });
  }

  function selectTab(nextTab) {
    const t = STAT_TABS.includes(nextTab) ? nextTab : "recipients";
    const seg = SEGMENT_FROM_TAB[t] || ALL_RECIPIENTS_SEGMENT;
    setActiveTab(t);
    setUrlState({ tab: t, windowDays });
    setAudienceSegment(seg);
    setAudiencePage(1);
    clearRetargetSelection();
    loadAudience({ resetPage: true, segment: seg });
    requestAnimationFrame(() => {
      audienceSectionRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  function updateWindowDays(nextDays) {
    const safe = parseWindowDaysParam(nextDays);
    setWindowDays(safe);
    setUrlState({ tab: activeTab, windowDays: safe });

    // Reply window only matters for canonical reply-based segments.
    if (audienceSegment === "REPLIED" || audienceSegment === "READ_NOT_REPLIED") {
      setAudiencePage(1);
      clearRetargetSelection();
      loadAudience({ resetPage: true, windowDays: safe });
    }
  }

  const counts = useMemo(() => getCampaignOutcomeCounts(summary), [summary]);

  const cardMeta = useMemo(() => {
    const total = counts.recipients || 0;
    const getPct = (x) => {
      if (!total) return 0;
      const p = (safeNumber(x) / safeNumber(total)) * 100;
      if (!Number.isFinite(p)) return 0;
      return Math.max(0, Math.min(100, p));
    };

    return {
      recipients: { pct: 100, count: total },
      awaitingDelivery: { pct: getPct(counts.awaitingDelivery), count: counts.awaitingDelivery },
      delivered: { pct: getPct(counts.delivered), count: counts.delivered },
      read: { pct: getPct(counts.read), count: counts.read },
      clicked: { pct: getPct(counts.clicks), count: counts.clicks },
      replied: { pct: getPct(counts.replied), count: counts.replied },
      failed: { pct: getPct(counts.failed), count: counts.failed },
    };
  }, [counts]);

  const retargetSegment = RETARGETABLE_SEGMENTS.has(audienceSegment)
    ? audienceSegment
    : null;
  const usesReplyWindowControls =
    audienceSegment === "REPLIED" || audienceSegment === "READ_NOT_REPLIED";

  const attributeDefinitionsById = useMemo(() => {
    const map = new Map();
    for (const definition of attributeDefinitions || []) {
      if (!definition?.id) continue;
      map.set(String(definition.id), definition);
    }
    return map;
  }, [attributeDefinitions]);

  const attributeConditionRows = useMemo(
    () =>
      (attributeConditions || []).map((condition) => {
        const definition = attributeDefinitionsById.get(String(condition.fieldId || ""));
        const dataType = definition?.dataType || "Text";
        const operators =
          ATTRIBUTE_OPERATORS_BY_TYPE[dataType] || ATTRIBUTE_OPERATORS_BY_TYPE.Text;
        const normalizedOperator = operators.includes(condition.operator)
          ? condition.operator
          : operators[0];

        return {
          ...condition,
          definition,
          dataType,
          operators,
          normalizedOperator,
          requiresValue:
            normalizedOperator !== "is_empty" && normalizedOperator !== "is_not_empty",
          isBoolean: dataType === "Boolean",
          isDate: dataType === "Date",
          isSelect: dataType === "SingleSelect" || dataType === "MultiSelect",
          isNumber: dataType === "Number",
        };
      }),
    [attributeConditions, attributeDefinitionsById]
  );

  const retargetPreviewKey = useMemo(
    () =>
      JSON.stringify({
        bucket: retargetSegment || "",
        repliedWindowDays: windowDays,
        conditions: attributeConditionRows.map((condition) => ({
          fieldId: condition.fieldId || "",
          operator: condition.normalizedOperator || "equals",
          value: condition.value || "",
        })),
      }),
    [attributeConditionRows, retargetSegment, windowDays]
  );

  const retargetAttributeSummary = useMemo(() => {
    if (!attributeConditionRows.length) {
      return "No Attribute narrowing applied.";
    }

    const parts = attributeConditionRows
      .map((condition) => {
        const label = condition.definition?.label || "Attribute";
        const operator =
          ATTRIBUTE_OPERATOR_LABELS[condition.normalizedOperator] ||
          condition.normalizedOperator;

        if (
          condition.normalizedOperator === "is_empty" ||
          condition.normalizedOperator === "is_not_empty"
        ) {
          return `${label} ${operator.toLowerCase()}`;
        }

        const value = String(condition.value || "").trim();
        if (!value) return null;
        return `${label} ${operator.toLowerCase()} ${value}`;
      })
      .filter(Boolean);

    if (!parts.length) return "No Attribute narrowing applied.";
    return `Match all: ${parts.join(" • ")}`;
  }, [attributeConditionRows]);

  async function loadSummary() {
    if (!campaignId) return;
    setSummaryLoading(true);
    try {
      const res = await getCampaignReportSummary(campaignId, {
        repliedWindowDays: windowDays ?? undefined,
        windowDays,
      });
      const data = res?.data?.data ?? res?.data ?? null;
      setSummary(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load campaign report.");
    } finally {
      setSummaryLoading(false);
    }
  }

  async function loadCampaign() {
    if (!campaignId) return;
    try {
      const res = await axiosClient.get(`/campaign/${campaignId}`, {
        __silent: true,
      });
      const data = res?.data?.data ?? res?.data ?? null;
      setCampaignMeta(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status !== 404 && status !== 405) console.error(err);
      setCampaignMeta(null);
    }
  }

  function extractItems(raw) {
    if (!raw) return [];
    const list =
      raw.items ||
      raw.Items ||
      raw.contacts ||
      raw.Contacts ||
      raw.rows ||
      raw.Rows ||
      [];
    return Array.isArray(list) ? list : [];
  }

  function normalizeAudienceResponse(raw, { page, pageSize }) {
    const items = extractItems(raw);
    const totalCount = extractCampaignAudienceTotalCount(raw);
    return {
      items,
      totalCount,
      page: Number.isFinite(Number(raw?.page ?? raw?.Page))
        ? Number(raw?.page ?? raw?.Page)
        : Number(page || 1),
      pageSize: Number.isFinite(Number(raw?.pageSize ?? raw?.PageSize))
        ? Number(raw?.pageSize ?? raw?.PageSize)
        : Number(pageSize || AUDIENCE_PAGE_SIZE),
    };
  }

  function formatAxiosError(err) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    let msg = "";
    if (typeof data === "string") msg = data;
    else if (typeof data?.message === "string") msg = data.message;
    else {
      try {
        msg = data ? JSON.stringify(data) : "";
      } catch {
        msg = "";
      }
    }
    if (msg && msg.length > 220) msg = `${msg.slice(0, 220)}...`;
    return { status, message: msg || err?.message || "" };
  }

  function getAudienceDisplayStatus(x) {
    const resolved = x?.displayStatus ?? x?.DisplayStatus ?? "";
    if (String(resolved).trim()) return String(resolved).trim();

    const readAt = tryParseDate(x?.readAt ?? x?.ReadAt ?? null);
    if (readAt) return "Read";

    const deliveredAt = tryParseDate(x?.deliveredAt ?? x?.DeliveredAt ?? null);
    if (deliveredAt) return "Delivered";

    const raw = x?.lastStatus ?? x?.status ?? x?.sendStatus ?? "";
    const normalized = String(raw || "").trim();
    if (!normalized) return "-";

    const upper = normalized.toUpperCase();
    if (upper === "FAILED" || upper === "FAIL" || upper.includes("ERROR")) {
      return "Failed";
    }
    if (upper.includes("REPLIED") || upper.includes("REPLY")) {
      return "Replied";
    }
    if (upper.includes("CLICK")) {
      return "URL Clicked";
    }
    if (upper.includes("READ")) {
      return "Read";
    }
    if (upper.includes("DELIVERED")) {
      return "Delivered";
    }
    if (upper.includes("SENT")) {
      return "Sent";
    }

    return normalized;
  }

  async function fetchAudience({
    segment,
    windowDays: wd,
    q,
    page,
    pageSize,
  }) {
    const query = String(q || "").trim();
    try {
      const res = await getCampaignBucketContacts(campaignId, segment, {
        q: query,
        page,
        pageSize,
        repliedWindowDays: wd,
      });
      const raw = res?.data?.data ?? res?.data ?? {};
      const normalized = normalizeAudienceResponse(raw, { page, pageSize });
      return { ...normalized, source: "campaign-logs" };
    } catch (err) {
      console.error("Audience fetch failed", {
        campaignId,
        segment,
        error: err,
      });

      throw new Error(
        err?.response?.data?.message ||
        err?.message ||
        "Audience unavailable. Failed to load canonical campaign contacts."
      );
    }
  }

  async function loadAudience({
    nextPage,
    resetPage,
    segment,
    windowDays: windowDaysOverride,
    q,
  } = {}) {
    if (!campaignId) return;
    const page = resetPage ? 1 : nextPage ?? audiencePage;
    const segmentToUse = segment ?? audienceSegment;
    const windowDaysToUse = windowDaysOverride ?? windowDays;
    const queryToUse = q ?? audienceSearch;
    setAudienceLoading(true);
    setAudienceError(null);
    try {
      const res = await fetchAudience({
        segment: segmentToUse,
        windowDays: windowDaysToUse,
        q: queryToUse,
        page,
        pageSize: AUDIENCE_PAGE_SIZE,
      });
      setAudienceItems(res.items || []);
      setAudienceTotalCount(res.totalCount);
      setAudienceSource(res.source || null);
      setAudienceHasLoaded(true);
      setAudiencePage(page);
      audienceAppliedRef.current = {
        segment: segmentToUse,
        search: queryToUse,
        windowDays: windowDaysToUse,
      };
    } catch (err) {
      console.error(err);
      const { status, message } = formatAxiosError(err);
      setAudienceItems([]);
      setAudienceTotalCount(null);
      setAudienceSource(null);
      setAudienceHasLoaded(true);
      setAudienceError(message || err?.message || "Failed to load contacts.");
      toast.error(
        `Failed to load contacts${status ? ` (${status})` : ""}${
          message ? `: ${message}` : ""
        }`
      );
    } finally {
      setAudienceLoading(false);
    }
  }

  function updateAttributeCondition(id, patch) {
    setAttributeConditions((prev) =>
      (prev || []).map((condition) =>
        condition.id === id ? { ...condition, ...patch } : condition
      )
    );
  }

  function addAttributeCondition() {
    setIsAttributesOpen(true);
    setAttributeConditions((prev) => [...(prev || []), createAttributeCondition()]);
  }

  function removeAttributeCondition(id) {
    setAttributeConditions((prev) =>
      (prev || []).filter((condition) => condition.id !== id)
    );
  }

  const clearRetargetSelection = useCallback(() => {
    selectedAudienceRowsRef.current = new Map();
    setSelectedContactIds(new Set());
  }, []);

  function getContactId(x) {
    return (
      x?.contactId ||
      x?.id ||
      x?.phone ||
      x?.contactPhone ||
      x?.recipientNumber ||
      x?.to ||
      x?.recipient ||
      null
    );
  }

  function getAudienceRowPhone(x) {
    return (
      x?.phone ||
      x?.contactPhone ||
      x?.recipientNumber ||
      x?.to ||
      x?.recipient ||
      ""
    );
  }

  function normalizeAudiencePhone(value) {
    return String(value || "").replace(/\D+/g, "");
  }

  function mapAudienceItemToRetargetRecipient(item) {
    return {
      contactId: item?.contactId || item?.contact?.id || null,
      contactName:
        item?.name || item?.contactName || item?.contact?.name || "Unknown",
      contactPhone: getAudienceRowPhone(item) || "-",
      recipientNumber:
        item?.recipientNumber || item?.to || item?.recipient || getAudienceRowPhone(item) || null,
      lastStatus: getAudienceDisplayStatus(item),
      lastActivityAt:
        item?.lastActivityAt ||
        item?.lastUpdatedAt ||
        item?.updatedAt ||
        item?.sentAt ||
        item?.createdAt ||
        null,
    };
  }

  async function handlePreviewRetargetAudience() {
    if (!campaignId) return;
    if (!retargetSegment) {
      setRetargetPreviewError(
        "Retarget preview is available only for Delivered not read, URL Clicked, Button Clicked, Read not replied, Failed, and Replied."
      );
      return;
    }

    const invalidCondition = attributeConditionRows.find((row) => {
      if (!row.definition) return true;
      if (!row.requiresValue) return false;
      return String(row.value || "").trim().length === 0;
    });

    if (invalidCondition) {
      const label = invalidCondition.definition?.label || "Attribute";
      toast.warn(`Complete the ${label} condition before previewing.`);
      return;
    }

    setRetargetPreviewLoading(true);
    setRetargetPreviewError(null);
    try {
      const payload = {
        sourceCampaignId: campaignId,
        sourceBucket: retargetSegment,
        repliedWindowDays: windowDays,
        attributeConditions: attributeConditionRows.map((row) => ({
          fieldId: row.fieldId,
          operator: row.normalizedOperator,
          value:
            row.normalizedOperator === "is_empty" || row.normalizedOperator === "is_not_empty"
              ? null
              : row.value ?? "",
        })),
      };

      const res = await previewCampaignRetargetAudience(campaignId, payload);
      const data = res?.data?.data ?? res?.data ?? null;
      setRetargetPreviewResult(data);
      setRetargetPreviewResolvedAt(new Date().toISOString());
      clearRetargetSelection();
      if (!Number(data?.resolvedCount || 0)) {
        setRetargetPreviewError("No recipients matched the current retarget filters.");
      }
    } catch (err) {
      console.error(err);
      const data = err?.response?.data;
      const msg =
        data?.message ||
        data?.error ||
        data?.title ||
        (typeof data === "string" ? data : null) ||
        err?.message ||
        "Failed to preview retarget audience.";
      setRetargetPreviewResult(null);
      setRetargetPreviewResolvedAt(null);
      setRetargetPreviewError(msg);
      toast.error(msg);
    } finally {
      setRetargetPreviewLoading(false);
    }
  }

  function toggleContactSelection(item) {
    const id = getContactId(item);
    if (!id) return;
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      const willSelect = !next.has(id);
      if (willSelect) {
        next.add(id);
        selectedAudienceRowsRef.current.set(String(id), mapAudienceItemToRetargetRecipient(item));
      } else {
        next.delete(id);
        selectedAudienceRowsRef.current.delete(String(id));
      }
      return next;
    });
  }

  function toggleSelectAll() {
    const itemsOnPage = audienceItems || [];
    const idsOnPage = itemsOnPage.map(getContactId).filter(Boolean);
    const allSelected = idsOnPage.every((id) => selectedContactIds.has(id));

    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        idsOnPage.forEach((id) => {
          next.delete(id);
          selectedAudienceRowsRef.current.delete(String(id));
        });
      } else {
        itemsOnPage.forEach((item) => {
          const id = getContactId(item);
          if (!id) return;
          next.add(id);
          selectedAudienceRowsRef.current.set(
            String(id),
            mapAudienceItemToRetargetRecipient(item)
          );
        });
      }
      return next;
    });
  }

  function mapAudienceRows(items) {
    return (items || []).map((x) => {
      const name = x?.name || x?.contactName || x?.contact?.name || "";
      const phone =
        x?.phone ||
        x?.contactPhone ||
        x?.recipientNumber ||
        x?.to ||
        x?.recipient ||
        "";
      const lastActivityAt =
        x?.lastActivityAt ||
        x?.lastUpdatedAt ||
        x?.updatedAt ||
        x?.sentAt ||
        x?.createdAt ||
        "";
      const status = getAudienceDisplayStatus(x);
      return {
        "Campaign Name": campaignName,
        "Campaign Id": campaignId,
        "Contact Name": name,
        "Contact Phone": x?.contactPhone || x?.phone || "",
        "Recipient Phone": x?.recipientNumber || x?.to || phone,
        Segment: x?.segment || audienceSegment,
        Status: status,
        "Sent At": x?.sentAt || "",
        "Delivered At": x?.deliveredAt || "",
        "Read At": x?.readAt || "",
        "Last Activity At": lastActivityAt,
        "MessageLog Id": x?.messageLogId || "",
        "CampaignSendLog Id": x?.sendLogId || x?.id || "",
        Clicked: x?.isClicked ? "Yes" : "No",
        "Click Type": x?.clickType || "",
        "Clicked At": x?.clickedAt || "",
        "Error Message": x?.errorMessage || "",
      };
    });
  }

  function downloadCsv(rows, { filename }) {
    const escapeCell = (v) => {
      const s = String(v ?? "");
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = [
      "Campaign Name",
      "Campaign Id",
      "Contact Name",
      "Contact Phone",
      "Recipient Phone",
      "Segment",
      "Status",
      "Sent At",
      "Delivered At",
      "Read At",
      "Last Activity At",
      "MessageLog Id",
      "CampaignSendLog Id",
      "Clicked",
      "Click Type",
      "Clicked At",
      "Error Message",
    ];
    const lines = [
      header.join(","),
      ...rows.map((r) => header.map((k) => escapeCell(r[k])).join(",")),
    ];
    const blob = new Blob([lines.join("\r\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function exportAudienceCsv() {
    if (!campaignId) return;
    if (audienceExporting) return;
    const MAX_EXPORT_ROWS = 5000;
    const EXPORT_PAGE_SIZE = 500;
    try {
      setAudienceExporting(true);
      const total = Number.isFinite(Number(audienceTotalCount))
        ? Number(audienceTotalCount)
        : null;

      // Default: export what the user can see right now.
      let exportItems = Array.isArray(audienceItems) ? [...audienceItems] : [];

      // If we know the total and it's reasonable, export ALL filtered contacts.
      if (total != null && total > exportItems.length) {
        if (total > MAX_EXPORT_ROWS) {
          toast.warn(
            `Export limited to current page (too many results: ${total.toLocaleString()}). Narrow filters to export all.`
          );
        } else {
          const pages = Math.max(1, Math.ceil(total / EXPORT_PAGE_SIZE));
          const all = [];
          for (let p = 1; p <= pages; p += 1) {
            const res = await fetchAudience({
              segment: audienceSegment,
              windowDays,
              q: audienceSearch,
              page: p,
              pageSize: EXPORT_PAGE_SIZE,
            });
            const items = Array.isArray(res?.items) ? res.items : [];
            all.push(...items);
          }
          exportItems = all;
        }
      }

      const rows = mapAudienceRows(exportItems.slice(0, MAX_EXPORT_ROWS));
      const filename = `campaign_${campaignId}_${audienceSegment}_${windowDays}d.csv`;
      downloadCsv(rows, { filename });
      toast.success(`Exported ${rows.length.toLocaleString()} contacts.`);
    } catch (err) {
      console.error(err);
      toast.error("Failed to export CSV.");
    } finally {
      setAudienceExporting(false);
    }
  }

  function goToRetarget() {
    if (!retargetEligibility.ok) return;
    if (retargetPreviewLoading) return;
    if (!retargetAudienceReady) {
      toast.warn("Preview the retarget audience on the page before launching.");
      return;
    }
    setIsRetargetModalOpen(true);
  }

  useEffect(() => {
    if (!tabFromUrl) setUrlState({ tab: activeTab, windowDays });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (STAT_TABS.includes(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      const nextSeg = SEGMENT_FROM_TAB[tabFromUrl] || ALL_RECIPIENTS_SEGMENT;
      setAudienceSegment(nextSeg);
      setAudiencePage(1);
      clearRetargetSelection();
      if (audienceHasLoaded) {
        loadAudience({ resetPage: true, segment: nextSeg });
      }
    }
    // loadAudience is intentionally omitted because this sync should react only to URL/tab state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl, activeTab, audienceHasLoaded]);

  useEffect(() => {
    if (windowDaysFromUrl !== windowDays) {
      setWindowDays(windowDaysFromUrl);
    }
  }, [windowDaysFromUrl, windowDays]);

  useEffect(() => {
    if (!allowed) return;
    loadCampaign();
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, campaignId, windowDays]);

  useEffect(() => {
    if (!businessId) return;
    setLoadingAttributeDefinitions(true);
    getCustomFieldDefinitions({ entityType: "Contact" })
      .then((definitions) => {
        setAttributeDefinitions(Array.isArray(definitions) ? definitions : []);
      })
      .catch((err) => {
        console.error("Failed to fetch retarget Attributes", err);
        setAttributeDefinitions([]);
        toast.error("Failed to load Attributes.");
      })
      .finally(() => setLoadingAttributeDefinitions(false));
  }, [businessId]);

  useEffect(() => {
    setRetargetPreviewResult(null);
    setRetargetPreviewError(null);
    setRetargetPreviewResolvedAt(null);
    clearRetargetSelection();
  }, [clearRetargetSelection, retargetPreviewKey]);

  useEffect(() => {
    // Only auto-load if the user has already loaded the audience once
    if (!audienceHasLoaded) return;
    const timer = setTimeout(() => {
      if (audienceSearch !== audienceAppliedRef.current.search) {
        loadAudience({ resetPage: true });
      }
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audienceSearch]);

  async function handleRefresh() {
    await Promise.all([loadCampaign(), loadSummary()]);
    if (audienceHasLoaded) {
      await loadAudience({ nextPage: audiencePage });
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
            <Skeleton className="h-10 w-32" />
          </div>
          <div className="grid grid-cols-7 gap-3">
            {[...Array(7)].map((_, i) => (
              <StatCardSkeleton key={i} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-6">
            <Skeleton className="h-96 rounded-3xl" />
            <div className="col-span-2 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <Skeleton className="h-24" />
                <Skeleton className="h-24" />
              </div>
              <Skeleton className="h-64 rounded-3xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="p-6">
        <div className="max-w-xl bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Campaign Log Report
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            You don't have permission to view this report.
          </p>
        </div>
      </div>
    );
  }

  const createdAt =
    campaignMeta?.createdAt ||
    campaignMeta?.created_at ||
    campaignMeta?.createdOn;
  const completedAt =
    campaignMeta?.completedAt ||
    campaignMeta?.completed_at ||
    campaignMeta?.completedOn ||
    campaignMeta?.updatedAt;

  const durationMs = (() => {
    const a = tryParseDate(createdAt);
    const b = tryParseDate(completedAt);
    if (!a || !b) return null;
    return b.getTime() - a.getTime();
  })();

  const campaignName =
    campaignMeta?.name ||
    campaignMeta?.campaignName ||
    campaignMeta?.title ||
    "Campaign Log Report";

  const templateName =
    campaignMeta?.templateName ||
    campaignMeta?.template?.name ||
    campaignMeta?.template ||
    null;

  const campaignType =
    campaignMeta?.campaignType ||
    campaignMeta?.type ||
    campaignMeta?.kind ||
    null;

  const status = campaignMeta?.status || campaignMeta?.state || null;
  const retargetEligibility = getRetargetEligibility(status);

  const audienceIsDirty =
    audienceHasLoaded &&
    (audienceAppliedRef.current.segment !== audienceSegment ||
      audienceAppliedRef.current.search !== audienceSearch ||
      audienceAppliedRef.current.windowDays !== windowDays);

  const AudiencePrimaryIcon = RefreshCcw;
  const audiencePrimaryLabel = audienceHasLoaded ? "Reload" : "Load contacts";
  const previewResolvedCount = Number(
    retargetPreviewResult?.resolvedAudienceCount ??
      retargetPreviewResult?.resolvedCount ??
      0
  );
  const previewCrmContactCount = Number(retargetPreviewResult?.crmContactCount || 0);
  const previewPhoneOnlyRecipientCount = Number(retargetPreviewResult?.phoneOnlyRecipientCount || 0);
  const previewInvalidPhoneCount = Number(retargetPreviewResult?.invalidPhoneCount || 0);
  const previewAttributeExcludedPhoneOnlyCount = Number(
    retargetPreviewResult?.attributeFilteredPhoneOnlyExcludedCount ??
      retargetPreviewResult?.skippedNoContactIdCount ??
      0
  );
  const previewContactIdSet = new Set(
    (Array.isArray(retargetPreviewResult?.resolvedContactIds)
      ? retargetPreviewResult.resolvedContactIds
      : []
    ).map((id) => String(id))
  );
  const previewRecipientPhoneSet = new Set(
    (Array.isArray(retargetPreviewResult?.resolvedRecipientPhones)
      ? retargetPreviewResult.resolvedRecipientPhones
      : []
    )
      .map((phone) => normalizeAudiencePhone(phone))
      .filter(Boolean)
  );
  const previewSampleRecipients = Array.isArray(retargetPreviewResult?.sampleRecipients)
    ? retargetPreviewResult.sampleRecipients
    : [];
  const selectedAudienceRecipients = (() => {
    const selectedRows = Array.from(selectedAudienceRowsRef.current.values());
    return selectedRows.filter((row) => {
      if (row?.contactId) {
        return previewContactIdSet.has(String(row.contactId));
      }
      const phone = normalizeAudiencePhone(row?.recipientNumber || row?.contactPhone || "");
      return phone ? previewRecipientPhoneSet.has(phone) : false;
    });
  })();
  const finalRetargetContactIds = (() => {
    if (selectedAudienceRecipients.length > 0) {
      return [...new Set(
        selectedAudienceRecipients
          .map((row) => row?.contactId)
          .filter(Boolean)
          .map((id) => String(id).trim())
      )];
    }

    return [...new Set(
      (Array.isArray(retargetPreviewResult?.resolvedContactIds)
        ? retargetPreviewResult.resolvedContactIds
        : []
      )
        .map((id) => String(id).trim())
        .filter(Boolean)
    )];
  })();
  const finalRetargetRecipientPhones = (() => {
    if (selectedAudienceRecipients.length > 0) {
      return [...new Set(
        selectedAudienceRecipients
          .filter((row) => !row?.contactId)
          .map((row) => normalizeAudiencePhone(row?.recipientNumber || row?.contactPhone || ""))
          .filter(Boolean)
      )];
    }

    return [...new Set(
      (Array.isArray(retargetPreviewResult?.resolvedRecipientPhones)
        ? retargetPreviewResult.resolvedRecipientPhones
        : []
      )
        .map((phone) => normalizeAudiencePhone(phone))
        .filter(Boolean)
    )];
  })();
  const usingSelectedSubset =
    selectedAudienceRecipients.length > 0 &&
    (finalRetargetContactIds.length > 0 || finalRetargetRecipientPhones.length > 0);
  const finalRetargetSampleRecipients =
    usingSelectedSubset && selectedAudienceRecipients.length > 0
      ? selectedAudienceRecipients
      : previewSampleRecipients;
  const retargetAudienceReady =
    !!retargetPreviewResult &&
    (finalRetargetContactIds.length > 0 || finalRetargetRecipientPhones.length > 0);
  const hasAttributeConditions = attributeConditionRows.length > 0;
  const retargetLaunchLabel = usingSelectedSubset
    ? `Retarget ${selectedAudienceRecipients.length.toLocaleString()} selected`
    : "Retarget";
  const retargetSummaryText = usingSelectedSubset
    ? `Using ${selectedAudienceRecipients.length.toLocaleString()} manually selected recipients from the resolved audience.`
    : `Using all ${previewResolvedCount.toLocaleString()} resolved recipients from the current preview.`;
  const retargetAudience = {
    isReady: retargetAudienceReady,
    sourceBucket: retargetSegment,
    segmentLabel: segmentLabel(retargetSegment || audienceSegment),
    resolvedCount: previewResolvedCount,
    selectedCount: selectedAudienceRecipients.length,
    usingSelectedSubset,
    contactIds: finalRetargetContactIds,
    recipientPhones: finalRetargetRecipientPhones,
    sampleRecipients: finalRetargetSampleRecipients,
    previewResolvedAt: retargetPreviewResolvedAt,
    attributeSummary: retargetAttributeSummary,
  };

  const performanceContext = (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="p-4">
        <div className="-mx-4 -mt-4 mb-4 flex items-start justify-between gap-3 border-b border-slate-200 bg-gradient-to-b from-white to-slate-50/70 p-4">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Outcomes details
            </h2>
            <p className="mt-1 text-[11px] text-slate-600">
              Lifecycle delivery and engagement signals for this campaign.
              Audience follow-up segments are separated below.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate(`/app/campaigns/logs/${campaignId}`)}
              className={reportButtonClass}
            >
              <FileSpreadsheet className="h-3.5 w-3.5 text-slate-500" />
              Open Raw Logs
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <Card className="rounded-lg p-4 lg:col-span-1">
            <div className="text-sm font-semibold text-slate-900">
              Campaign details
            </div>
            <div className="mt-2 divide-y divide-slate-100">
              <KV label="Campaign name" value={campaignName} />
              <KV label="Campaign type" value={campaignType || "-"} />
              <KV label="Status" value={status || "-"} />
              <KV
                label="Template name"
                value={templateName || "-"}
                copyable
              />
              <KV label="Created at" value={formatDateTime(createdAt)} />
              <KV
                label="Completed at"
                value={formatDateTime(completedAt)}
              />
              <KV
                label="Campaign duration"
                value={formatDurationMs(durationMs)}
              />
            </div>
          </Card>
          <div className="lg:col-span-2">
            <Card className="overflow-hidden rounded-lg p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-900">
                  Performance rates
                </div>
                <div className="flex items-center gap-3 text-[10px] font-medium text-slate-500">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    Success
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    Engagement
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    Failure
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="h-[230px] w-full">
                  <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Campaign Metrics
                  </p>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={[
                        { name: "Total", value: counts.recipients, fill: "#94a3b8" },
                        { name: "Pending", value: counts.awaitingDelivery, fill: "#94a3b8" },
                        { name: "Delivered", value: counts.delivered, fill: "#10b981" },
                        { name: "Read", value: counts.read, fill: "#3b82f6" },
                        { name: "Clicks", value: counts.clicks, fill: "#f59e0b" },
                        { name: "Failed", value: counts.failed, fill: "#ef4444" },
                      ]}
                      margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis
                        dataKey="name"
                        type="category"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 11, fontWeight: 500, fill: "#64748b" }}
                        width={110}
                      />
                      <Tooltip
                        cursor={{ fill: "transparent" }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            const pct = counts.recipients ? ((data.value / counts.recipients) * 100).toFixed(1) : 0;
                            return (
                              <div className="bg-white border border-slate-200 p-2 shadow-lg rounded-lg text-xs">
                                <p className="font-bold text-slate-900">{data.name}</p>
                                <p className="text-slate-600">Count: {data.value.toLocaleString()}</p>
                                <p className="text-emerald-600 font-semibold">{pct}% of total</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20}>
                        <LabelList
                          dataKey="value"
                          position="right"
                          formatter={(v) => v.toLocaleString()}
                          style={{ fontSize: 10, fontWeight: 600, fill: "#475569" }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="flex h-[230px] w-full flex-col">
                  <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Delivery Health
                  </p>
                  <div className="flex-1 flex items-center justify-center relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: "Delivered", value: counts.delivered, fill: "#10b981" },
                            { name: "Failed", value: counts.failed, fill: "#ef4444" },
                            { name: "Pending", value: counts.awaitingDelivery, fill: "#f1f5f9" },
                          ]}
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          <Cell key="cell-0" fill="#10b981" />
                          <Cell key="cell-1" fill="#ef4444" />
                          <Cell key="cell-2" fill="#f1f5f9" />
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0];
                              const total = counts.recipients || 1;
                              return (
                                <div className="bg-white border border-slate-200 p-2 shadow-lg rounded-lg text-xs">
                                  <p className="font-bold text-slate-900">{data.name}</p>
                                  <p className="text-slate-600">{data.value.toLocaleString()} ({((data.value / total) * 100).toFixed(1)}%)</p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          formatter={(value) => <span className="text-[11px] font-medium text-slate-600">{value}</span>}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute flex flex-col items-center justify-center">
                      <span className="text-xl font-bold text-slate-900">
                        {counts.recipients ? ((counts.delivered / counts.recipients) * 100).toFixed(0) : 0}%
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Success</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-2.5">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Delivered Rate</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-base font-bold text-emerald-600">
                      {counts.recipients ? ((counts.delivered / counts.recipients) * 100).toFixed(1) : 0}%
                    </span>
                    <span className="text-[10px] font-medium text-slate-400">({counts.delivered})</span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-2.5">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Read Rate</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-base font-bold text-blue-600">
                      {counts.recipients ? ((counts.read / counts.recipients) * 100).toFixed(1) : 0}%
                    </span>
                    <span className="text-[10px] font-medium text-slate-400">({counts.read})</span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-2.5">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Click Rate</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-base font-bold text-amber-600">
                      {counts.recipients ? ((counts.clicks / counts.recipients) * 100).toFixed(1) : 0}%
                    </span>
                    <span className="text-[10px] font-medium text-slate-400">({counts.clicks})</span>
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-100 bg-slate-50 p-2.5">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-tight">Failure Rate</p>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-base font-bold text-red-600">
                      {counts.recipients ? ((counts.failed / counts.recipients) * 100).toFixed(1) : 0}%
                    </span>
                    <span className="text-[10px] font-medium text-slate-400">({counts.failed})</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );

  const audienceWorkspace = (
    <>
      <div ref={audienceSectionRef} className="mt-5" />
      <Card className="overflow-hidden rounded-lg border border-slate-200 bg-white !p-0 shadow-sm">
        <div className="border-b border-slate-200 bg-gradient-to-b from-white to-slate-50/70 px-5 py-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="max-w-3xl">
              <h2 className="text-lg font-semibold text-slate-900">
                Audience Segments
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                Who to follow up with next using action buckets such as Delivered not
                read, Read not replied, URL Clicked, Button Clicked, Failed, and Replied. These segments
                are separate from the lifecycle outcomes above.
              </p>
            </div>
            <div className="flex items-center gap-2 self-start xl:self-auto">
              <button
                type="button"
                onClick={() =>
                  loadAudience({
                    resetPage: !audienceHasLoaded,
                    nextPage: audienceHasLoaded ? audiencePage : 1,
                  })
                }
                disabled={audienceLoading}
                title={audiencePrimaryLabel}
                aria-label={audiencePrimaryLabel}
                className={cx(
                  "inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm disabled:opacity-60",
                  audienceIsDirty
                    ? "ring-2 ring-emerald-100 hover:bg-slate-50"
                    : "hover:bg-slate-50"
                )}
              >
                <AudiencePrimaryIcon
                  className={cx("h-3.5 w-3.5", audienceLoading && "animate-spin")}
                />
              </button>
              <button
                type="button"
                onClick={exportAudienceCsv}
                disabled={
                  audienceExporting || !audienceHasLoaded || audienceItems.length === 0
                }
                title={
                  !audienceHasLoaded
                    ? "Load contacts first"
                    : audienceItems.length === 0
                      ? "No recipients to export"
                      : "Export CSV"
                }
                aria-label="Export CSV"
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
              >
                <FileSpreadsheet
                  className={cx(
                    "h-3.5 w-3.5 text-slate-500",
                    audienceExporting && "animate-pulse"
                  )}
                />
              </button>
              <button
                type="button"
                onClick={handlePreviewRetargetAudience}
                disabled={retargetPreviewLoading || !retargetSegment}
                title={
                  !retargetSegment
                    ? "Retarget is available for Delivered not read, URL Clicked, Button Clicked, Read not replied, Failed, and Replied."
                    : retargetPreviewLoading
                      ? "Confirming the audience"
                      : retargetAudienceReady
                        ? "Refresh the confirmed audience"
                        : "Confirm the audience"
                }
                className={reportButtonClass}
              >
                {retargetPreviewLoading ? (
                  <RefreshCcw className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Users className="h-3.5 w-3.5" />
                )}
                Confirm
              </button>
              <button
                type="button"
                onClick={goToRetarget}
                disabled={
                  !retargetEligibility.ok ||
                  !retargetAudienceReady ||
                  retargetPreviewLoading
                }
                className={reportButtonClass}
                title={
                  !retargetEligibility.ok
                    ? retargetEligibility.reason
                    : retargetPreviewLoading
                      ? "Resolving the retarget audience"
                    : !retargetAudienceReady
                      ? "Preview the retarget audience first"
                      : "Open retarget campaign setup"
                }
              >
                <Wand2 className="h-3.5 w-3.5" />
                {retargetLaunchLabel}
              </button>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              {AUDIENCE_SEGMENTS.map((segment) => {
                const active = audienceSegment === segment.key;
                return (
                  <button
                    key={segment.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() =>
                      selectTab(TAB_FROM_SEGMENT[segment.key] || "recipients")
                    }
                    className={cx(
                      reportButtonClass,
                      active
                        ? "!bg-emerald-50 !text-emerald-700"
                        : "text-slate-500 hover:text-slate-800"
                    )}
                  >
                    {segment.label}
                    {segment.key === "FAILED" ? (
                      <AlertTriangle className="h-3.5 w-3.5" />
                    ) : null}
                  </button>
                );
              })}
            </div>

            <div className="w-full xl:max-w-[360px]">
              <div className="flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 shadow-sm focus-within:border-emerald-500 focus-within:ring-4 focus-within:ring-emerald-500/10">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  value={audienceSearch}
                  onChange={(e) => setAudienceSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") loadAudience({ resetPage: true });
                  }}
                  placeholder="Search by contact name or phone"
                  className="w-full bg-transparent text-sm text-slate-900 outline-none"
                />
                {audienceSearch ? (
                  <button
                    type="button"
                    onClick={() => {
                      setAudienceSearch("");
                      loadAudience({ resetPage: true, q: "" });
                    }}
                    className="inline-flex items-center justify-center rounded-[8px] p-1 text-slate-500 hover:bg-slate-100"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {usesReplyWindowControls ? (
            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="w-full max-w-xs">
                <div className="mb-1.5 text-[11px] font-semibold text-slate-600">
                  Reply window
                </div>
                <select
                  value={windowDays}
                  onChange={(e) => updateWindowDays(e.target.value)}
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-normal text-slate-700 shadow-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10"
                >
                  <option value="all">All time</option>
                  <option value={1}>24 hours</option>
                  <option value={3}>3 days</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => setIsAttributesOpen(true)}
                disabled={loadingAttributeDefinitions || !retargetSegment}
                className={reportButtonClass}
              >
                {hasAttributeConditions ? "Edit Attributes" : "Add Attribute"}
              </button>
            </div>
          ) : null}

          {!usesReplyWindowControls || isAttributesOpen || hasAttributeConditions ? (
            <div className="mt-5 space-y-3 border-t border-slate-200 pt-4">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                {!usesReplyWindowControls ? (
                  <button
                    type="button"
                    onClick={() => setIsAttributesOpen((prev) => !prev)}
                    disabled={loadingAttributeDefinitions || !retargetSegment}
                    className={reportButtonClass}
                  >
                    {isAttributesOpen || hasAttributeConditions
                      ? "Edit Attributes"
                      : "Add Attributes filter"}
                  </button>
                ) : null}
                {hasAttributeConditions ? (
                  <div className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                    {retargetAttributeSummary}
                  </div>
                ) : null}
              </div>

              <div className="text-xs text-slate-500 xl:text-right">
                {!retargetSegment ? (
                  <span>
                    Retarget is available for <span className="font-semibold text-slate-700">Delivered not read</span>, <span className="font-semibold text-slate-700">URL Clicked</span>, <span className="font-semibold text-slate-700">Button Clicked</span>, <span className="font-semibold text-slate-700">Read not replied</span>, <span className="font-semibold text-slate-700">Failed</span>, and <span className="font-semibold text-slate-700">Replied</span>.
                  </span>
                ) : retargetAudienceReady ? (
                  <span>
                    {retargetSummaryText}
                    {retargetPreviewResolvedAt
                      ? ` Refreshed ${formatDateTime(retargetPreviewResolvedAt)}.`
                      : ""}
                  </span>
                ) : null}
              </div>
            </div>

            {loadingAttributeDefinitions ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-xs text-slate-500">
                Loading Attributes...
              </div>
            ) : null}

            {!loadingAttributeDefinitions && retargetSegment && !attributeDefinitions.length ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-xs text-slate-500">
                No active Contact Attributes are available yet.
              </div>
            ) : null}

            {isAttributesOpen ? (
              <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Narrow by Attributes
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Attributes are optional and use match-all in v1.1.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setIsAttributesOpen(false)}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Hide
                    </button>
                    <button
                      type="button"
                      onClick={addAttributeCondition}
                      disabled={
                        loadingAttributeDefinitions ||
                        !retargetSegment ||
                        !attributeDefinitions.length
                      }
                      className="inline-flex items-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                    >
                      Add Attribute
                    </button>
                  </div>
                </div>

                {attributeConditionRows.length > 0 ? (
                  <div className="space-y-3">
                    {attributeConditionRows.map((row, index) => (
                      <div
                        key={row.id}
                        className="rounded-xl border border-slate-200 bg-white p-3"
                      >
                        <div className="mb-3 flex items-center justify-between gap-2">
                          <div className="text-xs font-semibold text-slate-600">
                            Condition {index + 1}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttributeCondition(row.id)}
                            className="text-xs font-semibold text-rose-600 hover:text-rose-700"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                          <select
                            value={row.fieldId}
                            onChange={(e) => {
                              const nextFieldId = e.target.value;
                              const nextDefinition = attributeDefinitionsById.get(nextFieldId);
                              const nextOperators =
                                ATTRIBUTE_OPERATORS_BY_TYPE[nextDefinition?.dataType] ||
                                ATTRIBUTE_OPERATORS_BY_TYPE.Text;
                              updateAttributeCondition(row.id, {
                                fieldId: nextFieldId,
                                operator: nextOperators[0],
                                value: "",
                              });
                            }}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                          >
                            <option value="">Select Attribute</option>
                            {attributeDefinitions.map((definition) => (
                              <option key={definition.id} value={definition.id}>
                                {definition.label}
                              </option>
                            ))}
                          </select>

                          <select
                            value={row.normalizedOperator}
                            onChange={(e) =>
                              updateAttributeCondition(row.id, {
                                operator: e.target.value,
                                value:
                                  e.target.value === "is_empty" || e.target.value === "is_not_empty"
                                    ? ""
                                    : row.value,
                              })
                            }
                            disabled={!row.fieldId}
                            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                          >
                            {row.operators.map((operator) => (
                              <option key={operator} value={operator}>
                                {ATTRIBUTE_OPERATOR_LABELS[operator] || operator}
                              </option>
                            ))}
                          </select>

                          {row.requiresValue ? (
                            row.isBoolean ? (
                              <select
                                value={String(row.value ?? "")}
                                onChange={(e) =>
                                  updateAttributeCondition(row.id, {
                                    value: e.target.value,
                                  })
                                }
                                disabled={!row.fieldId}
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              >
                                <option value="">Select value</option>
                                <option value="true">True</option>
                                <option value="false">False</option>
                              </select>
                            ) : row.isDate ? (
                              <input
                                type="date"
                                value={row.value || ""}
                                onChange={(e) =>
                                  updateAttributeCondition(row.id, {
                                    value: e.target.value,
                                  })
                                }
                                disabled={!row.fieldId}
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              />
                            ) : row.isSelect ? (
                              <select
                                value={row.value || ""}
                                onChange={(e) =>
                                  updateAttributeCondition(row.id, {
                                    value: e.target.value,
                                  })
                                }
                                disabled={!row.fieldId}
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              >
                                <option value="">Select value</option>
                                {(row.definition?.options || []).map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={row.isNumber ? "number" : "text"}
                                value={row.value || ""}
                                onChange={(e) =>
                                  updateAttributeCondition(row.id, {
                                    value: e.target.value,
                                  })
                                }
                                placeholder={
                                  row.normalizedOperator === "contains"
                                    ? "Contains value"
                                    : "Enter value"
                                }
                                disabled={!row.fieldId}
                                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-emerald-500/20"
                              />
                            )
                          ) : (
                            <div className="flex items-center rounded-xl border border-dashed border-slate-300 bg-white px-3 py-2 text-sm text-slate-500">
                              No value needed
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                    Add one or more Attributes only if you need advanced narrowing.
                  </div>
                )}
              </div>
            ) : null}

            {(retargetPreviewError ||
              previewAttributeExcludedPhoneOnlyCount ||
              previewInvalidPhoneCount ||
              retargetAudienceReady) ? (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                  <span>
                    Resolved audience:{" "}
                    <span className="font-semibold text-slate-800">
                      {previewResolvedCount.toLocaleString()}
                    </span>
                  </span>
                  <span>
                    CRM contacts:{" "}
                    <span className="font-semibold text-slate-800">
                      {previewCrmContactCount.toLocaleString()}
                    </span>
                  </span>
                  <span>
                    Phone-only:{" "}
                    <span className="font-semibold text-slate-800">
                      {previewPhoneOnlyRecipientCount.toLocaleString()}
                    </span>
                  </span>
                  <span>
                    Launch audience:{" "}
                    <span className="font-semibold text-slate-800">
                      {retargetAudienceReady
                        ? usingSelectedSubset
                          ? `${selectedAudienceRecipients.length.toLocaleString()} selected`
                          : "All resolved recipients"
                        : "Preview required"}
                    </span>
                  </span>
                </div>

                {previewAttributeExcludedPhoneOnlyCount ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                    {previewAttributeExcludedPhoneOnlyCount.toLocaleString()} phone-only recipients were excluded because Contact Attribute filters apply only to CRM contacts.
                  </div>
                ) : null}

                {previewInvalidPhoneCount ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                    {previewInvalidPhoneCount.toLocaleString()} recipients were excluded because their phone number was missing or invalid.
                  </div>
                ) : null}

                {retargetPreviewError ? (
                  <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-semibold text-rose-700">
                    {retargetPreviewError}
                  </div>
                ) : null}
              </div>
            ) : null}
            </div>
          ) : null}

          {audienceSource === "mock" ? (
            <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Audience endpoint not available. Backend work required.
            </div>
          ) : null}
          {audienceError ? (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {audienceError}
            </div>
          ) : null}
        </div>

        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-100 text-left">
              <tr>
                <th className="w-10 px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    aria-label="Select all visible recipients"
                    className={roundCheckboxClass}
                    style={roundCheckboxStyle}
                    checked={
                      audienceItems.length > 0 &&
                      audienceItems.every((x) =>
                        selectedContactIds.has(getContactId(x))
                      )
                    }
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Contact
                </th>
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Segment
                </th>
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Delivery state
                </th>
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Last activity
                </th>
                <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody
              className={cx(
                "divide-y divide-slate-200",
                audienceLoading && audienceItems.length > 0 && "opacity-60"
              )}
            >
              {audienceLoading && audienceItems.length === 0 ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="bg-white">
                    <td className="px-4 py-2">
                      <div className="h-4 w-4 rounded bg-slate-100" />
                    </td>
                    {[...Array(5)].map((__, j) => (
                      <td key={j} className="px-4 py-2">
                        <div className="h-3 w-full max-w-[220px] rounded bg-slate-100" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : !audienceHasLoaded ? (
                <tr>
                  <td colSpan={6} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
                        <Users className="h-8 w-8 text-slate-300" />
                      </div>
                      <p className="text-base font-semibold text-slate-900">
                        Start with a recipient segment
                      </p>
                      <p className="mt-1 max-w-[320px] text-sm text-slate-500">
                        Choose a segment above and apply filters to load the
                        exact recipients you want to inspect or retarget.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : audienceItems.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                    <div className="text-sm font-medium text-slate-700">
                      No recipients found.
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      Try a different segment, adjust search, or reset the
                      audience workspace.
                    </div>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      {audienceSearch ? (
                        <button
                          type="button"
                          onClick={() => {
                            setAudienceSearch("");
                            loadAudience({ resetPage: true, q: "" });
                          }}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          Clear search
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          setAudienceSegment(ALL_RECIPIENTS_SEGMENT);
                          updateWindowDays(DEFAULT_WINDOW_DAYS);
                          setAudienceSearch("");
                          selectTab("recipients");
                          loadAudience({
                            resetPage: true,
                            segment: ALL_RECIPIENTS_SEGMENT,
                            q: "",
                            windowDays: DEFAULT_WINDOW_DAYS,
                          });
                        }}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Reset workspace
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                audienceItems.map((x, idx) => {
                  const contactId = x?.contactId || x?.contact?.id || null;
                  const name =
                    x?.name ||
                    x?.contactName ||
                    x?.contact?.name ||
                    "";
                  const phone =
                    x?.phone ||
                    x?.contactPhone ||
                    x?.recipientNumber ||
                    x?.to ||
                    x?.recipient ||
                    "-";
                  const nameIsPhone = /^[+\d\s().-]+$/.test(String(name)) &&
                    String(name).replace(/\D/g, "") === String(phone).replace(/\D/g, "");
                  const seg = x?.segment || audienceSegment;
                  const lastStatus = getAudienceDisplayStatus(x);
                  const lastActivityAt =
                    x?.lastActivityAt ||
                    x?.lastUpdatedAt ||
                    x?.updatedAt ||
                    x?.sentAt ||
                    x?.createdAt ||
                    null;

                  return (
                    <tr
                      key={x?.contactId || x?.id || phone || idx}
                      className={cx(
                        "bg-white transition-colors hover:bg-slate-50",
                        selectedContactIds.has(getContactId(x)) && "bg-emerald-50/50"
                      )}
                    >
                      <td className="px-4 py-2 align-middle">
                        <input
                          type="checkbox"
                          aria-label={`Select ${name || phone}`}
                          className={roundCheckboxClass}
                          style={roundCheckboxStyle}
                          checked={selectedContactIds.has(getContactId(x))}
                          onChange={() => toggleContactSelection(x)}
                        />
                      </td>
                      <td className="px-4 py-2">
                        <div className="min-w-[220px]">
                          <div className="font-normal text-slate-900">{name || phone}</div>
                          {name && phone !== "-" && !nameIsPhone && <div className="mt-1 font-normal text-xs text-slate-500">
                            {phone}
                          </div>}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <span className="text-sm font-normal text-slate-700">
                          {segmentLabel(seg)}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={cx(
                            "text-sm font-normal",
                            getAudienceStatusTone(lastStatus)
                          )}
                        >
                          {lastStatus}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-500">
                        {lastActivityAt ? formatDateTime(lastActivityAt) : "-"}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {contactId ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/app/crm/contacts/${contactId}`)}
                            className={reportButtonClass}
                          >
                            <Users className="h-3.5 w-3.5 text-slate-500" />
                            View contact
                          </button>
                        ) : (
                          <span className="text-xs text-slate-400">Phone-only</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="border-t border-slate-200 bg-white px-5 py-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs text-slate-600">
              {audienceTotalCount != null
                ? (() => {
                    const total = Number(audienceTotalCount);
                    const page = Number(audiencePage) || 1;
                    const start = total === 0 ? 0 : (page - 1) * AUDIENCE_PAGE_SIZE + 1;
                    const end =
                      total === 0 ? 0 : Math.min(page * AUDIENCE_PAGE_SIZE, total);
                    return `Showing ${start}-${end} of ${total.toLocaleString()} matching contacts${
                      audienceTotalPages ? ` • Page ${page} of ${audienceTotalPages}` : ""
                    }`;
                  })()
                : audienceSource
                  ? `Page ${audiencePage}`
                  : ""}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() =>
                  loadAudience({
                    nextPage: Math.max(1, audiencePage - 1),
                  })
                }
                disabled={audienceLoading || audiencePage <= 1}
                className={reportButtonClass}
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => loadAudience({ nextPage: audiencePage + 1 })}
                disabled={
                  audienceLoading ||
                  (audienceTotalPages != null && audiencePage >= audienceTotalPages)
                }
                className={reportButtonClass}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </Card>
    </>
  );

  return (
    <div className={`min-h-screen bg-slate-50 px-8 pb-6 pt-6 font-['Inter',ui-sans-serif,system-ui,sans-serif] text-sm sm:px-10 ${buttonTypographyClass}`}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out forwards;
        }
      `}</style>
      <div className="mx-auto flex max-w-[1600px] flex-col gap-4">
        <div className="animate-fade-in-up">
          <WorkspaceTitleHeader
            icon={BarChart3}
            title="Campaign Outcomes"
            subtitle={`${campaignName}${templateName ? ` • ${templateName}` : ""}`}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate("/app/campaigns/template-campaigns-list")}
                  className={reportButtonClass}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={summaryLoading}
                  aria-label="Refresh"
                  title="Refresh report"
                  className={`${reportButtonClass} w-9 !px-0`}
                >
                  <RefreshCcw
                    aria-hidden="true"
                    className={cx("h-4 w-4", summaryLoading && "animate-spin")}
                  />
                </button>
              </div>
            }
          />
        </div>

        <div className="space-y-4 py-1">
          {summaryLoading && !summary ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
              {[...Array(7)].map((_, i) => (
                <StatCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div
              role="group"
              aria-label="Campaign outcome KPIs"
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7"
            >
              <div
                className="animate-fade-in-up"
                style={{ animationDelay: "0ms" }}
              >
                <StatCard
                  title="Total"
                  percentage={cardMeta.recipients.pct}
                  count={cardMeta.recipients.count}
                  icon={Send}
                  iconClassName="bg-emerald-50 text-emerald-700"
                  ringColor="text-emerald-500"
                  helpText="Total campaign messages across all delivery outcomes."
                />
              </div>
              <div
                className="animate-fade-in-up"
                style={{ animationDelay: "50ms" }}
              >
                <StatCard
                  title="Delivered"
                  percentage={cardMeta.delivered.pct}
                  count={cardMeta.delivered.count}
                  icon={CheckCircle2}
                  iconClassName="bg-emerald-50 text-emerald-700"
                  ringColor="text-emerald-500"
                  helpText="WhatsApp delivery receipt was received. Delivered includes read messages."
                />
              </div>
              <div className="animate-fade-in-up" style={{ animationDelay: "75ms" }}>
                <StatCard
                  title="Pending"
                  percentage={cardMeta.awaitingDelivery.pct}
                  count={cardMeta.awaitingDelivery.count}
                  icon={Send}
                  iconClassName="bg-slate-50 text-slate-700"
                  ringColor="text-slate-500"
                  helpText="Messages without a delivered, read, or failed outcome yet."
                />
              </div>
              <div className="animate-fade-in-up" style={{ animationDelay: "100ms" }}>
                <StatCard
                  title="Failed"
                  percentage={cardMeta.failed.pct}
                  count={cardMeta.failed.count}
                  icon={AlertTriangle}
                  iconClassName="bg-red-50 text-red-700"
                  ringColor="text-red-500"
                  helpText="WhatsApp delivery failed."
                />
              </div>
              <div
                className="animate-fade-in-up"
                style={{ animationDelay: "100ms" }}
              >
                <StatCard
                  title="Read"
                  percentage={cardMeta.read.pct}
                  count={cardMeta.read.count}
                  icon={Eye}
                  iconClassName="bg-blue-50 text-blue-700"
                  ringColor="text-blue-500"
                  helpText="Read is included in Delivered."
                />
              </div>
              <div
                className="animate-fade-in-up"
                style={{ animationDelay: "150ms" }}
              >
                <StatCard
                  title="Clicks"
                  percentage={cardMeta.clicked.pct}
                  count={cardMeta.clicked.count}
                  icon={MousePointerClick}
                  iconClassName="bg-amber-50 text-amber-700"
                  ringColor="text-amber-500"
                  helpText="Messages with tracked URL or button clicks, counted once per message."
                />
              </div>
              <div
                className="animate-fade-in-up"
                style={{ animationDelay: "250ms" }}
              >
                <StatCard
                  title="Replies"
                  percentage={cardMeta.replied.pct}
                  count={cardMeta.replied.count}
                  icon={MessageCircle}
                  iconClassName="bg-cyan-50 text-cyan-700"
                  ringColor="text-cyan-500"
                  helpText="Recipient sent any inbound response attributed to this campaign."
                />
              </div>
            </div>
          )}
        </div>

        {performanceContext}
        {audienceWorkspace}

        <RetargetModal
          isOpen={isRetargetModalOpen}
          onClose={() => setIsRetargetModalOpen(false)}
          campaignId={campaignId}
          campaignStatus={status}
          campaignMeta={campaignMeta}
          retargetAudience={retargetAudience}
        />
      </div>
    </div>
  );
}
